import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import './Section8Case.css';
import { formatCurrency } from '../utils/currency';
import { formatDate, formatDateTime } from '../utils/dates';
import Stage6PCOL from './Stage6PCOL';
import { ArchiveButton, ArchivedBadge, RestoreButton } from './ArchiveControls';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_KEY = 'Bearer'; // Step 41 — auth via httpOnly cookie; this stub keeps existing headers harmless
const headers = { 'Authorization': API_KEY, 'Content-Type': 'application/json' };

const STAGE_LABELS = {
  3: 'Notice Preparation', 4: 'Notice Served', 5: 'Notice Period Active',
  6: 'Start Court Proceedings', 7: 'Hearing Preparation', 8: 'Outcome Recorded'
};

const DISCLAIMER = "Estatel AI populates documents based on information you have provided. This is not legal advice. If you are unsure of your legal position, please consult a solicitor.";

/** Controlled dd/mm/yyyy date input. Stores ISO (YYYY-MM-DD) in state. */
const UKDateInput = ({ value, onChange, ...props }) => {
  const isoToUK = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return (y && m && d) ? `${d}/${m}/${y}` : iso;
  };
  const ukToISO = (uk) => {
    const match = uk.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    return match ? `${match[3]}-${match[2]}-${match[1]}` : '';
  };
  const [display, setDisplay] = React.useState(isoToUK(value));

  React.useEffect(() => { setDisplay(isoToUK(value)); }, [value]);

  const handleChange = (e) => {
    let v = e.target.value.replace(/[^\d/]/g, '');
    // Auto-insert slashes
    const digits = v.replace(/\//g, '');
    if (digits.length <= 2) v = digits;
    else if (digits.length <= 4) v = digits.slice(0, 2) + '/' + digits.slice(2);
    else v = digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4, 8);
    setDisplay(v);
    const iso = ukToISO(v);
    if (iso) onChange({ target: { value: iso } });
    else if (v === '') onChange({ target: { value: '' } });
  };

  return <input type="text" inputMode="numeric" placeholder="dd/mm/yyyy" maxLength={10} value={display} onChange={handleChange} {...props} />;
};

const Section8Case = () => {
  const { case_id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [stageError, setStageError] = useState(null);

  // Centralised safe-fetch — reads the response body exactly once using text()
  // then JSON-parses, so callers never hit the "body stream already read"
  // pattern. Returns { ok, status, data } and surfaces stageError for non-2xx.
  const safeFetch = async (url, options = {}) => {
    const res = await fetch(url, options);
    let data = {};
    try {
      const txt = await res.text();
      data = txt ? JSON.parse(txt) : {};
    } catch (e) {
      data = {};
    }
    if (!res.ok) {
      setStageError(data.detail || data.message || `Request failed (HTTP ${res.status})`);
    }
    return { ok: res.ok, status: res.status, data };
  };

  // Stage 3 — inline record-service form (always visible until served).
  // These four state variables are used ONLY by the Stage 3 record-service
  // form. They MUST start empty and MUST NEVER be pre-populated from the
  // Case (regression-prone state-leak — the "Correct Service Date" panel
  // at Stage 4/5 has its own independent state below to guarantee this).
  const [serviceDate, setServiceDate] = useState('');
  const [serviceMethod, setServiceMethod] = useState('');  // empty by default — agent must choose
  const [serviceMethodOther, setServiceMethodOther] = useState('');
  const [serviceNotes, setServiceNotes] = useState('');
  const [recalculating, setRecalculating] = useState(false);
  // "Record Later" — when true the Stage 3 inline form is collapsed into a
  // compact link the agent can re-open at any time. Session-scoped only;
  // intentionally not persisted so on next page load the form is visible
  // again (so the agent never forgets to record service).
  const [recordLaterDismissed, setRecordLaterDismissed] = useState(false);

  // Stage 6
  const [pcolGuidance, setPcolGuidance] = useState(null);
  const [filingDeadline, setFilingDeadline] = useState('');
  const [pcolClaimNum, setPcolClaimNum] = useState('');
  const [uploadN5File, setUploadN5File] = useState(null);
  const [uploadN119File, setUploadN119File] = useState(null);
  const [uploadRentStmtFile, setUploadRentStmtFile] = useState(null);
  const [uploadTenancyFile, setUploadTenancyFile] = useState(null);
  // FIX 5 (2026-05-05) — Inline tenancy-agreement upload at Stage 6.
  // Opens a modal directly on the case page; no navigation away.
  const [showTenancyUploadModal, setShowTenancyUploadModal] = useState(false);
  const [inlineTenancyFile, setInlineTenancyFile] = useState(null);
  const [inlineTenancyUploading, setInlineTenancyUploading] = useState(false);
  const [inlineTenancyError, setInlineTenancyError] = useState(null);

  const handleInlineTenancyUpload = async () => {
    setInlineTenancyError(null);
    if (!inlineTenancyFile) {
      setInlineTenancyError('Please choose a PDF first.');
      return;
    }
    if (inlineTenancyFile.type !== 'application/pdf') {
      setInlineTenancyError('Only PDF files are accepted.');
      return;
    }
    if (!caseData?.property_id) {
      setInlineTenancyError('No property linked to this case.');
      return;
    }
    setInlineTenancyUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', inlineTenancyFile);
      const res = await fetch(
        `${BACKEND_URL}/api/properties/${caseData.property_id}/upload-tenancy-agreement`,
        { method: 'POST',
          headers: { 'Authorization': API_KEY },
          credentials: 'include',
          body: fd });
      const text = await res.text();
      const data = text ? JSON.parse(text) : {};
      if (!res.ok) {
        setInlineTenancyError(data.detail || `Upload failed (HTTP ${res.status})`);
        return;
      }
      setShowTenancyUploadModal(false);
      setInlineTenancyFile(null);
      await fetchCase();
    } catch (err) {
      setInlineTenancyError('Network error: ' + err.message);
    } finally {
      setInlineTenancyUploading(false);
    }
  };

  // Stage 7
  const [hearingDate, setHearingDate] = useState('');

  // Stage 8
  const [outcome, setOutcome] = useState('possession_granted');
  const [possessionDate, setPossessionDate] = useState('');
  const [conditions, setConditions] = useState('');
  const [dismissReason, setDismissReason] = useState('');
  const [newHearingDate, setNewHearingDate] = useState('');

  // Final possession
  const [finalPossDate, setFinalPossDate] = useState('');

  const fetchCase = useCallback(async () => {
    setLoading(true);
    try {
      const { ok, data } = await safeFetch(`${BACKEND_URL}/api/section8/case-detail/${case_id}`, { headers });
      if (ok) {
        setCaseData(data);
        if (data.court_filing_deadline) setFilingDeadline(data.court_filing_deadline);
        if (data.pcol_claim_number) setPcolClaimNum(data.pcol_claim_number);
      }
    } finally {
      setLoading(false);
    }
  }, [case_id]);

  useEffect(() => { fetchCase(); }, [fetchCase]);

  const fetchPcolGuidance = async () => {
    const { ok, data } = await safeFetch(`${BACKEND_URL}/api/section8/pcol-guidance/${case_id}`, { headers });
    if (ok) setPcolGuidance(data);
  };

  useEffect(() => {
    if (caseData && caseData.current_stage >= 6) {
      fetchPcolGuidance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData?.current_stage]);

  // STAGE 4: Record Service
  const handleRecordService = async () => {
    if (!serviceDate) return window.alert('Please enter a service date');
    if (!serviceMethod) return window.alert('Please select a method of service');
    if (serviceMethod === 'other' && !serviceMethodOther.trim()) {
      return window.alert('Please specify the method of service');
    }
    setProcessing(true); setStageError(null);
    try {
      // For "Other", fold the user's specification into the saved
      // notes so it round-trips into N215; the canonical method value
      // remains 'other' for downstream switching.
      const combinedNotes = (
        serviceMethod === 'other'
          ? `Method specified: ${serviceMethodOther.trim()}` +
            (serviceNotes.trim() ? `\n\n${serviceNotes.trim()}` : '')
          : serviceNotes
      );
      const { ok } = await safeFetch(`${BACKEND_URL}/api/section8/record-service`, {
        method: 'POST', headers,
        body: JSON.stringify({
          case_id, service_date: serviceDate,
          service_method: serviceMethod,
          notes: combinedNotes,
        })
      });
      if (ok) {
        // Reset the inline form. The form auto-hides once the case
        // refetch sees notice_served_date populated.
        setServiceDate('');
        setServiceMethod('');
        setServiceMethodOther('');
        setServiceNotes('');
        await fetchCase();
      }
    } finally { setProcessing(false); }
  };

  // STAGE 4 / STAGE 5: Recalculate notice period (used after any change to
  // the RRA-2025 notice-period table, or to refresh stale stored values).
  const handleRecalculateNoticePeriod = async () => {
    setRecalculating(true); setStageError(null);
    try {
      const { ok, data } = await safeFetch(
        `${BACKEND_URL}/api/section8/recalculate-notice-period/${case_id}`,
        { method: 'POST', headers }
      );
      if (ok) {
        await fetchCase();
        window.alert(data.message ||
          'Notice period recalculated. Countdown and expiry refreshed.');
      }
    } finally { setRecalculating(false); }
  };

  // STAGE 3 — Step 39: Redraft Notice. Only available once the case-detail
  // response says notice_service_status === 'overdue' (i.e. 48h+ elapsed and
  // service still not recorded). Voids the existing notice CaseDocument,
  // clears notice_generated_at on the case so the agent can regenerate, and
  // auto-dismisses the open service-window alerts.
  const handleRedraftNotice = async () => {
    const ok = window.confirm(
      "This will void the existing Section 8 notice and reset the case so you can generate a fresh notice with today's date. Continue?"
    );
    if (!ok) return;
    setProcessing(true); setStageError(null);
    try {
      const { ok: resOk } = await safeFetch(`${BACKEND_URL}/api/section8/redraft-notice`, {
        method: 'POST', headers, body: JSON.stringify({ case_id })
      });
      if (resOk) {
        // Send the agent back to the notice-generation flow rooted on the
        // tenancy AND on the existing case, so Section8.js can skip the
        // threshold + compliance checklist screens and land directly on the
        // grounds page (the case is still at Stage 3, grounds and ground
        // descriptions are retained on the case so /grounds will pre-populate
        // them).
        if (caseData?.tenancy_id) {
          navigate(`/section8/${caseData.tenancy_id}?redraft=1&case_id=${case_id}`);
        } else {
          await fetchCase();
        }
      }
    } finally { setProcessing(false); }
  };

  // STAGE 5: Notice period action — "Yes — Apply to Court" advances to Stage 6.
  // FIX 3 (2026-05-05): Route is determined automatically from the case
  // grounds — no agent selection screen. Rent grounds (8/10/11) → PCOL
  // online; everything else → paper-based.
  const determineProceedingRoute = (grounds) => {
    const g = Array.isArray(grounds) ? grounds.map(String) : [];
    const rentGrounds = ['8', '10', '11'];
    const hasRent = g.some(x => rentGrounds.includes(x));
    return hasRent ? 'pcol_online' : 'paper_based';
  };

  const handleNoticePeriodAction = async (action, proceeding_route = null) => {
    setProcessing(true); setStageError(null);
    try {
      const body = { case_id, action };
      if (action === 'apply_to_court') {
        // Auto-determine route from grounds when caller didn't specify one.
        body.proceeding_route = proceeding_route
          || determineProceedingRoute(caseData?.grounds);
      }
      const { ok } = await safeFetch(`${BACKEND_URL}/api/section8/notice-period-action`, {
        method: 'POST', headers,
        body: JSON.stringify(body),
      });
      if (ok) await fetchCase();
    } finally { setProcessing(false); }
  };

  // STAGE 3 (Step 48 FIX 1): Upload signed Section 8 notice PDF.
  const [signedNoticeFile, setSignedNoticeFile] = useState(null);
  const handleUploadSignedNotice = async () => {
    if (!signedNoticeFile) return window.alert('Select a PDF to upload first.');
    setProcessing(true); setStageError(null);
    try {
      const fd = new FormData();
      fd.append('file', signedNoticeFile);
      const res = await fetch(`${BACKEND_URL}/api/section8/upload-signed-notice/${case_id}`, {
        method: 'POST',
        headers: { 'Authorization': headers['Authorization'] },
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        setStageError(data.detail || 'Failed to upload signed notice.');
      } else {
        setSignedNoticeFile(null);
        await fetchCase();
      }
    } catch (e) {
      setStageError('Network error: ' + e.message);
    } finally { setProcessing(false); }
  };

  // STAGE 5 (Step 48 FIX 2): "Rent arrears or other grounds?" sub-question state.
  const [showRouteQuestion, setShowRouteQuestion] = useState(false);
  // STAGE 5 (Step 52 BUG 1): correct-service-date inline form toggle.
  const [showCorrectService, setShowCorrectService] = useState(false);
  // INDEPENDENT state for the Correct-Service-Date panel. Critical: must
  // NEVER share state with the Stage 3 record-service form above, otherwise
  // pre-populating these (with caseData values) leaks into the Stage 3 form.
  const [correctSvcDate, setCorrectSvcDate] = useState('');
  const [correctSvcMethod, setCorrectSvcMethod] = useState('');

  const handleCorrectServiceDate = async () => {
    if (!correctSvcDate) return window.alert('Enter a service date first.');
    setProcessing(true); setStageError(null);
    try {
      const { ok, data } = await safeFetch(`${BACKEND_URL}/api/section8/correct-service-date`, {
        method: 'POST', headers,
        body: JSON.stringify({ case_id, service_date: correctSvcDate, service_method: correctSvcMethod }),
      });
      if (ok) {
        window.alert(data?.message || 'Service date corrected.');
        setShowCorrectService(false);
        setCorrectSvcDate('');
        setCorrectSvcMethod('');
        await fetchCase();
      }
    } finally { setProcessing(false); }
  };

  // Step 52 BUG 5 — Change proceeding route at Stage 6.
  const handleChangeRoute = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to change your proceeding route? Any documents generated for the current route will be marked as superseded.'
    );
    if (!confirmed) return;
    setProcessing(true); setStageError(null);
    try {
      const { ok } = await safeFetch(`${BACKEND_URL}/api/section8/change-route/${case_id}`, {
        method: 'POST', headers, body: JSON.stringify({}),
      });
      if (ok) {
        // After clearing the route, we drop the user back to the Stage 5
        // route-question screen. Stage stays 6 server-side; the UI re-renders
        // the route picker because proceeding_route is now null.
        await fetchCase();
        setShowRouteQuestion(true);
      }
    } finally { setProcessing(false); }
  };

  // Step 52 BUG 5 — Route is locked once one of the route-specific finalisation
  // actions has been taken: PCOL claim submitted OR paper bundle posted.
  const routeLocked = !!(caseData?.pcol_claim_submitted || caseData?.n215_bundle_posted_date);

  // STAGE 6 paper-based — structured court address (Step 54).
  const [courtForm, setCourtForm] = useState({
    court_name: '', court_address_line1: '', court_address_line2: '',
    court_city: '', court_county: '', court_postcode: '',
    tenancy_start_override: '',
    notice_service_method: '',  // ITEM 2 — agent confirmation at Stage 6
  });
  const [paperEvidenceFile, setPaperEvidenceFile] = useState(null);
  const [feePaidDate, setFeePaidDate] = useState('');
  const [nohFile, setNohFile] = useState(null);
  const [nohExtracted, setNohExtracted] = useState(null);  // {claim_number, hearing_date}
  const [nohClaimNum, setNohClaimNum] = useState('');
  const [nohHearingDate, setNohHearingDate] = useState('');

  useEffect(() => {
    if (caseData) {
      setCourtForm(cf => ({
        court_name: caseData.court_name || cf.court_name || '',
        court_address_line1: caseData.court_address_line1 || cf.court_address_line1 || '',
        court_address_line2: caseData.court_address_line2 || cf.court_address_line2 || '',
        court_city: caseData.court_city || cf.court_city || '',
        court_county: caseData.court_county || cf.court_county || '',
        court_postcode: caseData.court_postcode || cf.court_postcode || '',
        tenancy_start_override: (
          caseData.tenancy_start_override
          || cf.tenancy_start_override
          || (caseData.tenancy_start_date
              ? String(caseData.tenancy_start_date).slice(0, 10)
              : '')
        ),
        notice_service_method: (
          caseData.notice_service_method
          || cf.notice_service_method
          || ''
        ),
      }));
    }
  }, [caseData?.court_name, caseData?.court_address_line1, caseData?.court_city, caseData?.court_postcode, caseData?.tenancy_start_override, caseData?.tenancy_start_date, caseData?.notice_service_method]);

  const handleSavePaperCourtAddress = async () => {
    if (!courtForm.court_name.trim() || !courtForm.court_address_line1.trim()
        || !courtForm.court_city.trim() || !courtForm.court_postcode.trim()) {
      return window.alert('Enter court name, address line 1, city and postcode.');
    }
    setProcessing(true); setStageError(null);
    try {
      const { ok } = await safeFetch(`${BACKEND_URL}/api/section8/stage6/court-address/${case_id}`, {
        method: 'POST', headers, body: JSON.stringify(courtForm),
      });
      if (ok) await fetchCase();
    } finally { setProcessing(false); }
  };

  const handleUploadPaperEvidence = async () => {
    if (!paperEvidenceFile) return window.alert('Select an evidence file first.');
    setProcessing(true); setStageError(null);
    try {
      const fd = new FormData();
      fd.append('file', paperEvidenceFile);
      const res = await fetch(`${BACKEND_URL}/api/section8/paper-bundle/upload-evidence/${case_id}`, {
        method: 'POST',
        headers: { 'Authorization': headers['Authorization'] },
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setStageError(data.detail || 'Evidence upload failed.');
      } else {
        setPaperEvidenceFile(null);
        await fetchCase();
      }
    } catch (e) {
      setStageError('Network error: ' + e.message);
    } finally { setProcessing(false); }
  };

  const handleGeneratePaperBundle = async () => {
    setProcessing(true); setStageError(null);
    try {
      const { ok, data } = await safeFetch(`${BACKEND_URL}/api/section8/stage6/generate-paper-bundle/${case_id}`, {
        method: 'POST', headers, body: JSON.stringify({}),
      });
      if (ok) {
        await fetchCase();
        if (data?.court_bundle_url) {
          window.open(`${BACKEND_URL}${data.court_bundle_url}`, '_blank');
        }
      }
    } finally { setProcessing(false); }
  };

  // Step 54 — Mark court fee paid.
  const handleMarkFeePaid = async () => {
    const d = feePaidDate || new Date().toISOString().slice(0, 10);
    setProcessing(true); setStageError(null);
    try {
      const { ok } = await safeFetch(`${BACKEND_URL}/api/section8/stage6/mark-fee-paid`, {
        method: 'POST', headers, body: JSON.stringify({ case_id, paid_date: d }),
      });
      if (ok) await fetchCase();
    } finally { setProcessing(false); }
  };

  // Step 54 — Upload Notice of Hearing and extract claim number + hearing date.
  const handleUploadNoH = async () => {
    if (!nohFile) return window.alert('Select the Notice of Hearing PDF first.');
    setProcessing(true); setStageError(null);
    try {
      const fd = new FormData();
      fd.append('file', nohFile);
      const res = await fetch(`${BACKEND_URL}/api/section8/stage6/upload-notice-of-hearing/${case_id}`, {
        method: 'POST',
        headers: { 'Authorization': headers['Authorization'] },
        credentials: 'include', body: fd,
      });
      const txt = await res.text();
      const data = txt ? JSON.parse(txt) : {};
      if (!res.ok) {
        setStageError(data.detail || 'Notice of Hearing upload failed.');
      } else {
        setNohExtracted(data.extracted || null);
        setNohClaimNum((data.extracted && data.extracted.claim_number) || '');
        setNohHearingDate((data.extracted && data.extracted.hearing_date) || '');
        await fetchCase();
      }
    } catch (e) {
      setStageError('Network error: ' + e.message);
    } finally { setProcessing(false); }
  };

  const handleConfirmNoH = async () => {
    if (!nohClaimNum.trim() || !nohHearingDate) {
      return window.alert('Confirm both the claim number and hearing date.');
    }
    setProcessing(true); setStageError(null);
    try {
      const { ok } = await safeFetch(`${BACKEND_URL}/api/section8/stage6/confirm-notice-of-hearing`, {
        method: 'POST', headers,
        body: JSON.stringify({ case_id, claim_number: nohClaimNum.trim(), hearing_date: nohHearingDate }),
      });
      if (ok) {
        window.alert('Hearing date saved. Case advanced to Stage 7.');
        await fetchCase();
      }
    } finally { setProcessing(false); }
  };

  const handleBackfillClaim = async () => {
    setProcessing(true); setStageError(null);
    try {
      const { ok, data } = await safeFetch(`${BACKEND_URL}/api/section8/stage6/backfill-claim-number/${case_id}`, {
        method: 'POST', headers, body: JSON.stringify({}),
      });
      if (ok) {
        window.alert(data?.message || 'N215 documents regenerated.');
        await fetchCase();
      }
    } finally { setProcessing(false); }
  };

  // Step 49 — Mark bundle as posted. Saves today's date as the N215 court-bundle
  // service date so the 24-hour posting reminder skips itself on next firing.
  const handleMarkBundlePosted = async () => {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    setProcessing(true); setStageError(null);
    try {
      const { ok } = await safeFetch(`${BACKEND_URL}/api/section8/record-n215-posted`, {
        method: 'POST', headers,
        body: JSON.stringify({ case_id, posted_date: today }),
      });
      if (ok) {
        window.alert('Bundle marked as posted. The 24 hour reminder will not fire.');
        await fetchCase();
      }
    } finally { setProcessing(false); }
  };

  // Step 51 — Undo "mark posted". Clears n215_bundle_posted_date and re-arms
  // the 24-hour reminder if the original was already skipped.
  const handleUndoBundlePosted = async () => {
    setProcessing(true); setStageError(null);
    try {
      const { ok } = await safeFetch(`${BACKEND_URL}/api/section8/record-n215-posted/${case_id}`, {
        method: 'DELETE', headers,
      });
      if (ok) {
        window.alert('Posting record cleared. The 24 hour reminder has been re-armed if it has not yet fired.');
        await fetchCase();
      }
    } finally { setProcessing(false); }
  };

  // STAGE 6: Mark claim submitted
  const handleMarkClaimSubmitted = async () => {
    setProcessing(true); setStageError(null);
    try {
      const { ok } = await safeFetch(`${BACKEND_URL}/api/section8/mark-claim-submitted`, {
        method: 'POST', headers, body: JSON.stringify({ case_id })
      });
      if (ok) await fetchCase();
    } finally { setProcessing(false); }
  };

  // STAGE 6: Upload a specific PCOL document
  const handleUploadSpecific = async (label, file) => {
    if (!file) return window.alert('Select a file first');
    setProcessing(true); setStageError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_label', label);
      const { ok } = await safeFetch(`${BACKEND_URL}/api/section8/upload-document/${case_id}`, {
        method: 'POST',
        headers: { 'Authorization': API_KEY },
        body: formData
      });
      if (ok) {
        if (label === 'n5_issued') setUploadN5File(null);
        if (label === 'n119_issued') setUploadN119File(null);
        if (label === 'pcol_rent_statement') setUploadRentStmtFile(null);
        if (label === 'tenancy_agreement') setUploadTenancyFile(null);
        await fetchCase();
      }
    } finally { setProcessing(false); }
  };

  const handleGenerateRentSchedule = async () => {
    setProcessing(true); setStageError(null);
    try {
      const { ok } = await safeFetch(`${BACKEND_URL}/api/section8/rent-schedule/${case_id}`, { headers });
      if (ok) await fetchCase();
    } finally { setProcessing(false); }
  };

  const handleSetDeadline = async () => {
    if (!filingDeadline) return;
    setStageError(null);
    await safeFetch(`${BACKEND_URL}/api/section8/set-court-deadline`, {
      method: 'POST', headers,
      body: JSON.stringify({ case_id, court_filing_deadline: filingDeadline })
    });
    await fetchCase();
  };

  const handleSetPcol = async () => {
    setStageError(null);
    await safeFetch(`${BACKEND_URL}/api/section8/set-pcol-claim`, {
      method: 'POST', headers,
      body: JSON.stringify({ case_id, pcol_claim_number: pcolClaimNum })
    });
    await fetchCase();
  };

  // STAGE 7
  const handleRecordHearing = async () => {
    if (!hearingDate) return window.alert('Enter hearing date');
    setProcessing(true); setStageError(null);
    try {
      const { ok } = await safeFetch(`${BACKEND_URL}/api/section8/record-hearing-date`, {
        method: 'POST', headers,
        body: JSON.stringify({ case_id, hearing_date: hearingDate })
      });
      if (ok) await fetchCase();
    } finally { setProcessing(false); }
  };

  const handleGenerateSummary = async () => {
    setProcessing(true); setStageError(null);
    try {
      const { ok } = await safeFetch(`${BACKEND_URL}/api/section8/generate-case-summary`, {
        method: 'POST', headers, body: JSON.stringify({ case_id })
      });
      if (ok) await fetchCase();
    } finally { setProcessing(false); }
  };

  // STAGE 8
  const handleRecordOutcome = async () => {
    setProcessing(true); setStageError(null);
    try {
      const body = { case_id, outcome };
      if (outcome === 'possession_granted') body.possession_date = possessionDate;
      if (outcome === 'suspended') body.conditions = conditions;
      if (outcome === 'dismissed') body.reason = dismissReason;
      if (outcome === 'adjourned') body.new_hearing_date = newHearingDate;
      const { ok } = await safeFetch(`${BACKEND_URL}/api/section8/record-outcome`, {
        method: 'POST', headers, body: JSON.stringify(body)
      });
      if (ok) await fetchCase();
    } finally { setProcessing(false); }
  };

  const handleRecordFinalPossession = async () => {
    if (!finalPossDate) return;
    setProcessing(true); setStageError(null);
    try {
      await safeFetch(`${BACKEND_URL}/api/section8/record-possession-date`, {
        method: 'POST', headers,
        body: JSON.stringify({ case_id, possession_date: finalPossDate })
      });
      await fetchCase();
    } finally { setProcessing(false); }
  };

  // AUDIT EXPORT
  const handleAuditExport = async () => {
    setProcessing(true); setStageError(null);
    try {
      const { ok, data } = await safeFetch(`${BACKEND_URL}/api/section8/audit-export`, {
        method: 'POST', headers, body: JSON.stringify({ case_id })
      });
      if (ok && data.file_url) {
        await downloadCaseDocument(data.file_url, 'case-audit.pdf');
        await fetchCase();
      }
    } finally { setProcessing(false); }
  };

  // Authenticated PDF download — case documents endpoints require the Bearer
  // token so we cannot just window.open() the raw file_url (which results in
  // {"detail":"Not authenticated"}). Fetch with the session header, turn the
  // body into a blob URL, and trigger a synthetic <a download> click.
  const downloadCaseDocument = async (fileUrl, suggestedName = 'document.pdf') => {
    try {
      const res = await fetch(`${BACKEND_URL}${fileUrl}`, { headers: { 'Authorization': API_KEY } });
      if (!res.ok) {
        const txt = await res.text();
        window.alert(`Unable to download document (${res.status}). ${txt || ''}`.trim());
        return;
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = suggestedName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Release the blob URL after the browser has had a chance to start the download.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      window.alert('Download failed: ' + err.message);
    }
  };

  if (loading) {
    return <div className="sc-container"><div className="sc-loading">Loading case...</div></div>;
  }
  if (!caseData) {
    return <div className="sc-container"><div className="sc-loading">Case not found.</div></div>;
  }

  const stage = caseData.current_stage;

  return (
    <div className="sc-container" data-testid="section8-case-page">
      <div className="sc-nav">
        <Link to="/" className="sc-back" data-testid="sc-back">Back to Dashboard</Link>
      </div>

      <div className="sc-disclaimer" data-testid="sc-disclaimer">{DISCLAIMER}</div>

      {stageError && (
        <div
          role="alert"
          data-testid="sc-stage-error"
          style={{
            margin: '1rem 0', padding: '0.75rem 1rem', borderRadius: 8,
            background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
          <span><strong>Action failed:</strong> {stageError}</span>
          <button
            type="button"
            onClick={() => setStageError(null)}
            style={{ background: 'transparent', border: 'none', color: '#991b1b', cursor: 'pointer', fontSize: '1.1rem' }}
            aria-label="Dismiss error"
          >×</button>
        </div>
      )}

      {/* CASE HEADER */}
      <div className="sc-header" data-testid="sc-header">
        <h1 className="sc-title">Section 8 Case</h1>
        <div className="sc-meta">
          <span className="sc-tenant">{caseData.tenant_name}</span>
          <span className="sc-property">{caseData.property_address}</span>
          <span className="sc-stage-badge">Stage {stage} — {STAGE_LABELS[stage] || `Stage ${stage}`}</span>
        </div>
        <div className="sc-stats">
          <div className="sc-stat">
            <span className="sc-stat-label">Total Arrears</span>
            <span className="sc-stat-value sc-red">{formatCurrency(caseData.total_arrears)}</span>
          </div>
          <div className="sc-stat">
            <span className="sc-stat-label">Landlord</span>
            <span className="sc-stat-value">{caseData.landlord_name}</span>
          </div>
          <div className="sc-stat">
            <span className="sc-stat-label">Regime</span>
            <span className="sc-stat-value">{caseData.legislation_regime?.replace('_', ' ')}</span>
          </div>
          {caseData.notice_countdown !== null && caseData.notice_countdown !== undefined && (
            <div className="sc-stat">
              <span className="sc-stat-label">Notice Period</span>
              <span className={`sc-stat-value ${caseData.notice_period_ended ? 'sc-green' : 'sc-amber'}`}>
                {caseData.notice_period_ended ? 'Ended' : `${caseData.notice_countdown} days`}
              </span>
            </div>
          )}
          {caseData.hearing_countdown !== null && caseData.hearing_countdown !== undefined && (
            <div className="sc-stat">
              <span className="sc-stat-label">Hearing</span>
              <span className={`sc-stat-value ${caseData.hearing_alert === 'red' ? 'sc-red' : caseData.hearing_alert === 'amber' ? 'sc-amber' : ''}`}>
                {caseData.hearing_countdown} days
              </span>
            </div>
          )}
        </div>

        {caseData.court_deadline_alert === 'urgent' && (
          <div className="sc-urgent-alert" data-testid="sc-court-deadline-alert">
            Court filing deadline is within 7 days! Check your deadline and submit promptly.
          </div>
        )}

        {caseData.arrears_below_threshold && stage >= 5 && stage < 8 && (
          <div className="sc-advisory" data-testid="sc-partial-payment-advisory">
            <strong>Advisory:</strong> Arrears have dropped below the mandatory threshold. However, the claim is live. Grounds 10 and 11 remain active regardless of the current balance. The judge retains full discretion.
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            className="sc-btn sc-btn-outline"
            style={{ background: '#0073EA', color: '#fff', border: 'none' }}
            onClick={handleAuditExport}
            disabled={processing}
            data-testid="sc-btn-audit">
            {processing ? 'Exporting...' : 'Export Full Case Audit'}
          </button>
          {/* Step 53 BUG 1 — Change Route lives next to Export Full Case Audit
              so it's prominent. Visible only at Stage 6 with a chosen route
              and before the route-locking actions (PCOL submitted /
              bundle posted). Red to flag it as a destructive action. */}
          {stage === 6 && caseData.proceeding_route && !routeLocked && (
            <button
              type="button"
              onClick={handleChangeRoute}
              disabled={processing}
              data-testid="sc-btn-change-route"
              style={{
                background: '#dc2626', color: '#fff', border: 'none',
                padding: '0.55rem 1.1rem', borderRadius: 6, fontWeight: 500,
                cursor: processing ? 'not-allowed' : 'pointer',
              }}>
              Change Route
            </button>
          )}
        </div>
      </div>

      {/* STAGE-SPECIFIC CONTENT */}
      <div className="sc-stage-content">

        {/* STAGE 3: Notice generated — agent must serve, then advance to
            Stage 4 to record service via the dropdown panel. */}
        {stage === 3 && (
          <div className="sc-section" data-testid="sc-stage-3">
            <h2>Stage 3 — Notice Preparation Complete</h2>
            <p>Your Section 8 notice has been generated. Print and sign it,
               serve it on the tenant, then move to Stage 4 to record service.</p>

            {/* 2026-05-06 (item 3) — Amber regeneration advisory.
                Surfaces ONLY when:
                  • the backend has flagged `previous_service_cleared=true`
                    on the case (set by /section8/generate-notice when a
                    pre-existing service date was wiped during regen), AND
                  • no new service entry has been recorded yet.
                The flag is automatically cleared on /section8/record-service
                so this banner disappears the moment the agent re-records
                service. It never appears for first-time notices. */}
            {caseData.previous_service_cleared && !caseData.notice_served_date && (
              <div
                data-testid="sc-notice-regenerated-banner"
                style={{
                  margin: '0 0 1rem 0', padding: '0.85rem 1rem', borderRadius: 8,
                  border: '1px solid #fcd34d', background: '#fffbeb', color: '#92400e',
                }}>
                <strong>New notice generated</strong> — the previous service
                date and method have been cleared. Please re-record service
                for this new notice once it has been served on the tenant.
              </div>
            )}

            {/* Step 39 — Service-window banner. Amber while pending or under
                24h+, red after 48h with no service recorded. */}
            {caseData.notice_service_status && (
              <div
                data-testid="sc-notice-service-banner"
                style={{
                  margin: '0 0 1rem 0', padding: '0.85rem 1rem', borderRadius: 8,
                  border: '1px solid',
                  background: caseData.notice_service_status === 'overdue' ? '#fef2f2' : '#fff7ed',
                  borderColor: caseData.notice_service_status === 'overdue' ? '#fecaca' : '#fed7aa',
                  color: caseData.notice_service_status === 'overdue' ? '#991b1b' : '#9a3412',
                }}>
                <strong>
                  {caseData.notice_service_status === 'overdue'
                    ? 'Service window expired (>48h):'
                    : caseData.notice_service_status === 'reminder'
                      ? 'Service reminder (24h+):'
                      : 'Service pending:'}
                </strong>{' '}
                {caseData.notice_service_status === 'overdue'
                  ? 'service has not been recorded within 48 hours of generation. The notice may need to be redrafted with today\'s date unless service has already taken place.'
                  : caseData.notice_service_status === 'reminder'
                    ? 'this notice was generated more than 24 hours ago. Please serve and log the service date today — you have until 48 hours from generation before the notice will need to be redrafted.'
                    : 'serve this notice on the tenant promptly and record the service date at Stage 4. The notice must be served within 48 hours of generation to remain valid.'}
                {' '}<em style={{ display: 'block', marginTop: '0.4rem', fontSize: '0.85rem', opacity: 0.85 }}>Legal disclaimer: This is not legal advice. If you are unsure of your position please consult a solicitor.</em>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {/* Step 39 — Re-issue S8 Notice (only after 48h with no service) */}
              {caseData.notice_service_status === 'overdue' && (
                <button
                  className="sc-btn"
                  style={{ background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer' }}
                  onClick={handleRedraftNotice}
                  disabled={processing}
                  data-testid="sc-btn-redraft-notice">
                  {processing ? 'Voiding…' : 'Re-issue S8 Notice'}
                </button>
              )}
            </div>

            {/* 2026-05-06 (Item 5) — Signing-instructions note. Tells the
                agent who must sign the printed Section 8 notice before
                serving it on the tenant. Wording follows landlord_type:
                  • company    → "[director_name] as Director of [company_name]"
                  • individual → "[landlord.full_name]"
                Text Field 119 (signature) is left blank in the PDF — the
                agent or landlord signs by hand. */}
            {(() => {
              const lt = (caseData.landlord_type || 'individual').toLowerCase();
              const llFull = caseData.landlord_full_name || caseData.landlord_name || '';
              const llCompany = caseData.landlord_company_name || '';
              const director = caseData.landlord_director_name || '';
              if (lt === 'company') {
                if (!director || !llCompany) return null;
                return (
                  <div
                    data-testid="sc-signing-instructions"
                    style={{
                      margin: '1rem 0 0', padding: '0.85rem 1rem',
                      borderRadius: 8, border: '1px solid #cbd5e1',
                      background: '#f8fafc', color: '#0f172a',
                      fontSize: '0.9rem', lineHeight: 1.5,
                    }}>
                    This notice must be signed by <strong>{director}</strong>{' '}
                    as Director of <strong>{llCompany}</strong> before
                    serving on the tenant.
                  </div>
                );
              }
              if (!llFull) return null;
              return (
                <div
                  data-testid="sc-signing-instructions"
                  style={{
                    margin: '1rem 0 0', padding: '0.85rem 1rem',
                    borderRadius: 8, border: '1px solid #cbd5e1',
                    background: '#f8fafc', color: '#0f172a',
                    fontSize: '0.9rem', lineHeight: 1.5,
                  }}>
                  This notice must be signed by <strong>{llFull}</strong>{' '}
                  before serving on the tenant.
                </div>
              );
            })()}

            {/* Stage 3 — Inline always-visible Record Service form. Once
                the notice has been generated this form lives directly on
                the page until the agent records the service date and
                method. No button to open it; nothing pre-filled.
                Save persists to the Case and advances to Stage 4. The
                "Record Later" button collapses the form for this session
                so the agent can come back and complete it later. */}
            {!caseData.notice_served_date && recordLaterDismissed && (
              <div
                className="sc-subsection"
                data-testid="sc-record-service-collapsed"
                style={{
                  marginTop: '1.25rem',
                  padding: '0.9rem 1.1rem',
                  background: '#f8fafc',
                  border: '1px dashed #cbd5e1',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '1rem'
                }}>
                <span style={{ color: '#475569', fontSize: '0.9rem' }}>
                  Service of the Section 8 Notice has not yet been recorded.
                </span>
                <button
                  type="button"
                  onClick={() => setRecordLaterDismissed(false)}
                  data-testid="sc-btn-record-service-now"
                  style={{
                    background: '#0073EA', color: '#fff', border: 'none',
                    padding: '0.5rem 1rem', borderRadius: 6,
                    cursor: 'pointer', fontWeight: 500, fontSize: '0.88rem'
                  }}>
                  Record Service Now
                </button>
              </div>
            )}

            {!caseData.notice_served_date && !recordLaterDismissed && (
              <div
                className="sc-subsection"
                data-testid="sc-record-service-panel"
                style={{
                  marginTop: '1.25rem',
                  padding: '1.5rem',
                  background: '#fff',
                  border: '1px solid #cbd5e1',
                  borderRadius: 8,
                  boxShadow: '0 2px 6px rgba(15, 23, 42, 0.06)'
                }}>
                <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#0f172a', fontSize: '1.05rem' }}>
                  Record Service of Section 8 Notice
                </h3>

                <div style={{ marginBottom: '1rem' }}>
                  <label
                    htmlFor="sc-rs-date"
                    style={{ display: 'block', marginBottom: '0.4rem',
                             fontSize: '0.9rem', color: '#334155', fontWeight: 500 }}>
                    Date you served this notice on the tenant
                  </label>
                  <UKDateInput
                    id="sc-rs-date"
                    value={serviceDate}
                    onChange={e => setServiceDate(e.target.value)}
                    data-testid="sc-record-service-date"
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label
                    htmlFor="sc-rs-method"
                    style={{ display: 'block', marginBottom: '0.4rem',
                             fontSize: '0.9rem', color: '#334155', fontWeight: 500 }}>
                    How was the notice served?
                  </label>
                  <select
                    id="sc-rs-method"
                    value={serviceMethod}
                    onChange={e => setServiceMethod(e.target.value)}
                    data-testid="sc-record-service-method"
                    style={{
                      width: '100%',
                      padding: '0.55rem 0.7rem',
                      borderRadius: 6,
                      border: '1px solid #cbd5e1',
                      background: '#fff',
                      fontSize: '0.92rem',
                      color: serviceMethod ? '#0f172a' : '#94a3b8',
                    }}>
                    <option value="" disabled>— Select method —</option>
                    <option value="personal_service">By hand (personal delivery)</option>
                    <option value="first_class_post">By first class post</option>
                    <option value="other">Other (please specify)</option>
                  </select>

                  {serviceMethod === 'other' && (
                    <input
                      type="text"
                      placeholder="Specify the method"
                      value={serviceMethodOther}
                      onChange={e => setServiceMethodOther(e.target.value)}
                      data-testid="sc-record-service-method-other-text"
                      style={{ marginTop: '0.6rem', width: '100%',
                               padding: '0.55rem 0.7rem', borderRadius: 6,
                               border: '1px solid #cbd5e1', fontSize: '0.92rem' }}
                    />
                  )}
                </div>

                <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={handleRecordService}
                    disabled={
                      processing || !serviceDate || !serviceMethod ||
                      (serviceMethod === 'other' && !serviceMethodOther.trim())
                    }
                    data-testid="sc-btn-record-service-save"
                    style={{
                      background: '#16a34a', color: '#fff', border: 'none',
                      padding: '0.6rem 1.4rem', borderRadius: 6,
                      fontWeight: 500, fontSize: '0.92rem',
                      cursor: (processing || !serviceDate || !serviceMethod ||
                               (serviceMethod === 'other' && !serviceMethodOther.trim()))
                        ? 'not-allowed' : 'pointer',
                      opacity: (processing || !serviceDate || !serviceMethod ||
                               (serviceMethod === 'other' && !serviceMethodOther.trim()))
                        ? 0.55 : 1
                    }}>
                    {processing ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Discard any half-typed values so the form stays
                      // pristine for next time. The form NEVER auto-fills.
                      setServiceDate('');
                      setServiceMethod('');
                      setServiceMethodOther('');
                      setServiceNotes('');
                      setRecordLaterDismissed(true);
                    }}
                    disabled={processing}
                    data-testid="sc-btn-record-service-later"
                    style={{
                      background: '#fff', color: '#0073EA',
                      border: '1px solid #0073EA',
                      padding: '0.6rem 1.4rem', borderRadius: 6,
                      fontWeight: 500, fontSize: '0.92rem',
                      cursor: processing ? 'not-allowed' : 'pointer'
                    }}>
                    Record Later
                  </button>
                </div>

                {/* Persistent advisory shown below the form whenever the
                    notice has been generated but no service has been
                    recorded. The notification reminder system already
                    polls Case.notice_served_date — this banner just
                    surfaces the same expectation in the UI. */}
                <div
                  data-testid="sc-record-service-reminder"
                  style={{
                    marginTop: '1rem', padding: '0.75rem 0.9rem',
                    background: '#fff7ed', border: '1px solid #fed7aa',
                    borderRadius: 6, color: '#9a3412',
                    fontSize: '0.88rem', lineHeight: 1.5
                  }}>
                  Please record the service date and method before
                  proceeding. Estatel AI will begin sending notification
                  reminders until this is completed.
                </div>
              </div>
            )}

            {/* Step 48 (FIX 1) — Upload Signed Notice. Visible until a signed copy
                is on file; once uploaded the section converts to a confirmation
                badge. The signed copy replaces the unsigned generated version
                in all court bundles. */}
            <div
              className="sc-subsection"
              data-testid="sc-upload-signed-notice"
              style={{
                marginTop: '1.5rem',
                padding: '1.25rem',
                background: caseData.signed_notice_uploaded ? '#ecfdf5' : '#fff',
                border: '1px solid',
                borderColor: caseData.signed_notice_uploaded ? '#bbf7d0' : '#e2e8f0',
                borderRadius: 8,
              }}>
              <h3 style={{ marginTop: 0, color: '#0f172a' }}>Upload Signed Notice</h3>
              {!caseData.signed_notice_uploaded ? (
                <>
                  <p style={{ color: '#475569', fontSize: '0.92rem', lineHeight: 1.55 }}>
                    Once you have printed, signed, and served this notice on the tenant,
                    upload the signed copy here. This signed copy will be used in your court bundle.
                  </p>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={e => setSignedNoticeFile(e.target.files[0])}
                      data-testid="sc-signed-notice-file"
                    />
                    <button
                      type="button"
                      onClick={handleUploadSignedNotice}
                      disabled={processing || !signedNoticeFile}
                      data-testid="sc-btn-upload-signed-notice"
                      style={{
                        background: '#0073EA', color: '#fff', border: 'none',
                        padding: '0.55rem 1.1rem', borderRadius: 6, fontWeight: 500,
                        cursor: processing || !signedNoticeFile ? 'not-allowed' : 'pointer',
                      }}>
                      {processing ? 'Uploading…' : 'Upload Signed Notice'}
                    </button>
                  </div>
                </>
              ) : (
                <div
                  data-testid="sc-signed-notice-uploaded-badge"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.55rem',
                    background: '#10b981', color: '#fff', fontWeight: 600,
                    padding: '0.55rem 1rem', borderRadius: 999, fontSize: '0.92rem',
                  }}>
                  <span>Signed Notice Uploaded</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* STAGE 4 — Read-only summary of the recorded service.
            All record-service entry happens at Stage 3 above. Stage 4 is
            now purely informational: served date + method, earliest
            possession date, countdown, and a Recalculate button. */}
        {stage === 4 && (
          <div className="sc-section" data-testid="sc-stage-4">
            <h2>Stage 4 — Notice Served</h2>
            <p data-testid="sc-stage-4-served-summary">
              <strong>Notice served:</strong>{' '}
              {caseData.notice_served_date}{' '}
              <strong>by</strong>{' '}
              {(caseData.notice_method || '').replace('_', ' ')}
            </p>
            {caseData.notice_service_notes && (
              <p data-testid="sc-stage-4-served-notes"
                 style={{ color: '#475569', fontSize: '0.9rem',
                          whiteSpace: 'pre-wrap' }}>
                <strong>Notes:</strong> {caseData.notice_service_notes}
              </p>
            )}
            <p>
              Earliest date court proceedings can begin:{' '}
              <strong>{caseData.notice_period_end}</strong>
            </p>
            <div className="sc-countdown" data-testid="sc-countdown">
              Notice period ends in <strong>{caseData.notice_countdown}</strong> days
            </div>
            <div style={{ marginTop: '0.8rem' }}>
              <button
                type="button"
                onClick={handleRecalculateNoticePeriod}
                disabled={recalculating}
                data-testid="sc-btn-recalc-notice-period-stage4"
                style={{
                  background: '#fff', color: '#0073EA',
                  border: '1px solid #0073EA',
                  padding: '0.5rem 1rem', borderRadius: 6,
                  cursor: recalculating ? 'wait' : 'pointer',
                  fontSize: '0.88rem'
                }}>
                {recalculating ? 'Recalculating…' : 'Recalculate Notice Period'}
              </button>
            </div>
          </div>
        )}

        {/* STAGE 5: Notice period active */}
        {stage === 5 && (
          <div className="sc-section" data-testid="sc-stage-5">
            <h2>Stage 5 — Notice Period Active</h2>
            {/* FIX 6 — Start Court Proceedings is the primary action.
                Always visible once service has been recorded; clicking
                opens the route picker which routes through Stage 6 with
                the warning banner when the notice period has not yet
                expired (FIX 5). */}
            <div data-testid="sc-stage-5-primary" style={{
                   display: 'flex', flexDirection: 'column', gap: '0.6rem',
                   margin: '0.5rem 0 1.25rem' }}>
              <button
                type="button"
                onClick={() => handleNoticePeriodAction('apply_to_court')}
                disabled={processing}
                data-testid="sc-btn-start-court-proceedings"
                style={{
                  background: '#0073EA', color: '#fff', border: 'none',
                  padding: '0.95rem 1.35rem', borderRadius: 8,
                  fontWeight: 600, fontSize: '1.05rem',
                  cursor: processing ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 16px rgba(0,115,234,0.18)',
                  alignSelf: 'flex-start',
                }}>
                Start Court Proceedings
              </button>
              {/* Notice period countdown sits below the primary CTA. */}
              {!caseData.notice_period_ended && (
                <div className="sc-countdown" data-testid="sc-countdown-5">
                  Notice period ends <strong>{caseData.notice_period_end}</strong>{' '}
                  — <strong>{caseData.notice_countdown} days remaining</strong>
                </div>
              )}
              {/* Secondary action — Audit export */}
              <button
                type="button"
                onClick={handleAuditExport}
                disabled={processing}
                data-testid="sc-btn-stage5-audit-export"
                style={{
                  background: '#fff', color: '#0073EA',
                  border: '1px solid #0073EA',
                  padding: '0.4rem 0.85rem', borderRadius: 6,
                  fontSize: '0.85rem', cursor: 'pointer',
                  alignSelf: 'flex-start',
                }}>
                {processing ? 'Exporting…' : 'Export Full Case Audit'}
              </button>
            </div>
            {!caseData.notice_period_ended ? (
              <>
                <div style={{ marginTop: '0.6rem', marginBottom: '0.6rem' }}>
                  <button
                    type="button"
                    onClick={handleRecalculateNoticePeriod}
                    disabled={recalculating}
                    data-testid="sc-btn-recalc-notice-period-stage5"
                    style={{
                      background: '#fff', color: '#0073EA',
                      border: '1px solid #0073EA',
                      padding: '0.5rem 1rem', borderRadius: 6,
                      cursor: recalculating ? 'wait' : 'pointer',
                      fontSize: '0.88rem'
                    }}>
                    {recalculating ? 'Recalculating…' : 'Recalculate Notice Period'}
                  </button>
                </div>
                {/* Step 52 BUG 1 — Correct Service Date. Always available while
                    the notice period has not yet ended (stage 4 or 5). Lets an
                    agent fix a typo (most common: wrong year) without rolling
                    the case back. Pre-populated with the recorded values. */}
                <div className="sc-subsection" data-testid="sc-correct-service" style={{ marginTop: '1.25rem', padding: '1.1rem', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                  {!showCorrectService ? (
                    <button
                      type="button"
                      onClick={() => {
                        setShowCorrectService(true);
                        // Pre-populate the *correct-service* panel only —
                        // these states are independent from the Stage 3
                        // record-service form and never leak back into it.
                        setCorrectSvcDate(caseData.notice_served_date || '');
                        setCorrectSvcMethod(caseData.notice_method || '');
                      }}
                      data-testid="sc-btn-show-correct-service"
                      style={{ background: 'none', border: 'none', padding: 0, color: '#0073EA', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.92rem' }}>
                      Service date wrong? Correct it
                    </button>
                  ) : (
                    <>
                      <h3 style={{ marginTop: 0 }}>Correct Service Date</h3>
                      <p style={{ color: '#475569', fontSize: '0.9rem' }}>
                        Use this if you entered the wrong service date when recording service. The notice period
                        will be re-calculated. Only available while the notice period has not yet ended.
                      </p>
                      <div className="sc-form-group">
                        <label>Date of Service</label>
                        <UKDateInput value={correctSvcDate} onChange={e => setCorrectSvcDate(e.target.value)} data-testid="sc-correct-service-date" />
                      </div>
                      <div className="sc-form-group">
                        <label>Method of Service</label>
                        <select value={correctSvcMethod} onChange={e => setCorrectSvcMethod(e.target.value)} data-testid="sc-correct-service-method">
                          <option value="" disabled>— Select method —</option>
                          <option value="personal_service">By hand (personal delivery)</option>
                          <option value="first_class_post">By first class post</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          type="button"
                          onClick={handleCorrectServiceDate}
                          disabled={processing}
                          data-testid="sc-btn-correct-service"
                          style={{ background: '#0073EA', color: '#fff', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 6, cursor: 'pointer' }}>
                          {processing ? 'Saving…' : 'Save Correction'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowCorrectService(false);
                            setCorrectSvcDate('');
                            setCorrectSvcMethod('');
                          }}
                          disabled={processing}
                          data-testid="sc-btn-cancel-correct-service"
                          style={{ background: '#e2e8f0', color: '#1e293b', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 6, cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : null}
            {/* FIX 5/6 — Route picker is always available once Start Court
                Proceedings has been clicked, regardless of notice-period
                expiry. When ended, the original "Apply to Court" sub-flow
                still wraps it. */}
            {showRouteQuestion && (
              <div data-testid="sc-route-question" style={{ marginTop: '1rem',
                     padding: '1rem 1.25rem', background: '#f8fafc',
                     border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <h4 style={{ margin: '0 0 0.5rem 0' }}>
                  Are you proceeding based on rent arrears or other grounds?
                </h4>
                <p style={{ color: '#475569', fontSize: '0.92rem', lineHeight: 1.55 }}>
                  Your selection determines whether the court bundle uses
                  the PCOL online flow (rent arrears) or the paper-based
                  N5/N119 flow (other grounds).
                </p>
                <div style={{ display: 'flex', flexDirection: 'column',
                              gap: '0.5rem', marginTop: '0.5rem' }}>
                  <button
                    type="button"
                    onClick={() => handleNoticePeriodAction('apply_to_court', 'pcol_online')}
                    disabled={processing}
                    data-testid="sc-btn-route-pcol-stage5"
                    style={{
                      background: '#0073EA', color: '#fff', border: 'none',
                      padding: '0.85rem 1.2rem', borderRadius: 6, fontWeight: 500,
                      cursor: 'pointer', textAlign: 'left',
                    }}>
                    <strong>Rent arrears</strong> — Grounds 8, 10 or 11 selected. Use the PCOL online submission flow.
                  </button>
                  <button
                    type="button"
                    onClick={() => handleNoticePeriodAction('apply_to_court', 'paper_based')}
                    disabled={processing}
                    data-testid="sc-btn-route-paper-stage5"
                    style={{
                      background: '#0f172a', color: '#fff', border: 'none',
                      padding: '0.85rem 1.2rem', borderRadius: 6, fontWeight: 500,
                      cursor: 'pointer', textAlign: 'left',
                    }}>
                    <strong>Other grounds only</strong> — Ground 12, 14, 17 or any non-rent ground. Use the paper-based application flow.
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowRouteQuestion(false)}
                    disabled={processing}
                    data-testid="sc-btn-route-back-stage5"
                    style={{
                      background: '#e2e8f0', color: '#1e293b', border: 'none',
                      padding: '0.55rem 1.1rem', borderRadius: 6, cursor: 'pointer',
                      alignSelf: 'flex-start',
                    }}>
                    Back
                  </button>
                </div>
              </div>
            )}
            {caseData.notice_period_ended && !showRouteQuestion && (
              <div className="sc-period-ended" data-testid="sc-period-ended">
                <h3>Notice period has ended for {caseData.tenant_name}.</h3>
                <p>You may now begin court proceedings or withdraw the case.</p>
                <div className="sc-actions">
                  <button
                    className="sc-btn sc-btn-secondary"
                    onClick={() => handleNoticePeriodAction('withdraw')}
                    disabled={processing}
                    data-testid="sc-btn-withdraw">
                    No — Withdraw / Continue Monitoring
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* STAGE 6: Court Filing — branched by Case.proceeding_route (Step 48 FIX 3 + FIX 4) */}
        {/* Step 52 BUG 5 — When the agent clicks Change Route, proceeding_route
            is cleared on the server. We re-show the same route-picker UI used
            on Stage 5 so the agent can choose afresh, while keeping
            current_stage at 6.
            2026-05-06 — Picker now pre-selects the recommended route based
            on Stage 3's funnel choice (case.case_flow): PCOL for Funnel A
            (rent arrears), Paper for Funnel B (all grounds). The agent can
            still switch; switching after a previous court_route was saved
            triggers a confirmation dialog. Confirmed selection is saved to
            Case.court_route as 'paper' or 'pcol'. */}
        {stage === 6 && !caseData.proceeding_route && (
          <Stage6RoutePicker
            caseData={caseData}
            processing={processing}
            onConfirm={async (route) => {
              // Confirmation dialog only fires when the agent is switching
              // away from a previously-saved court_route.
              if (caseData.court_route && caseData.court_route !== route) {
                if (!window.confirm(
                  'Changing route will reset your Stage 6 progress for this case. Are you sure?'
                )) return;
              }
              setProcessing(true);
              try {
                const res = await fetch(`${BACKEND_URL}/api/section8/confirm-court-route`, {
                  method: 'POST', credentials: 'include',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ case_id: case_id, route }),
                });
                if (res.ok) await fetchCase();
                else window.alert('Could not confirm route. Please try again.');
              } finally {
                setProcessing(false);
              }
            }}
          />
        )}

        {stage === 6 && caseData.proceeding_route !== 'paper_based' && caseData.proceeding_route && (() => {
          // FIX 5 — same persistent amber banner for the PCOL route.
          const npe = caseData.notice_period_end ? new Date(caseData.notice_period_end) : null;
          const expired = npe && npe.getTime() <= Date.now();
          const days = npe ? Math.max(0, Math.ceil((npe.getTime() - Date.now()) / 86400000)) : null;
          const fmt = (d) => d ? new Date(d).toLocaleDateString('en-GB',
            { day: 'numeric', month: 'long', year: 'numeric' }) : '';
          if (npe && !expired) {
            return (
              <div data-testid="advance-prep-banner-pcol"
                   style={{ background: '#fff7ed', border: '1px solid #fdba74',
                            color: '#9a3412', padding: '0.85rem 1.1rem',
                            borderRadius: 8, margin: '0 0 1rem',
                            fontSize: '0.92rem' }}>
                <strong>Notice period still running.</strong> The notice period
                for this tenancy has not yet expired. Notice period ends on{' '}
                <strong>{fmt(caseData.notice_period_end)}</strong> —{' '}
                <strong>{days} day{days === 1 ? '' : 's'} remaining</strong>.
                Court proceedings should not be issued until this date.
                You are preparing the bundle in advance.
              </div>
            );
          }
          return null;
        })()}
        {stage === 6 && caseData.proceeding_route !== 'paper_based' && caseData.proceeding_route && (
          <div className="sc-section" data-testid="sc-stage-6">
            <h2>Stage 6 — Start Court Proceedings (PCOL Online)</h2>
            <Stage6PCOL caseId={case_id} caseData={caseData} onChanged={fetchCase} />
          </div>
        )}
        {false && stage === 6 && caseData.proceeding_route !== 'paper_based' && caseData.proceeding_route && (
          <div className="sc-section" data-testid="sc-stage-6-legacy-pcol-hidden">
            <h2>Stage 6 — Start Court Proceedings (PCOL online — rent arrears)</h2>

            {/* PHASE 1: PCOL Guidance */}
            <div className="sc-subsection" data-testid="sc-phase-1">
              <h3>Phase 1 — Submit Claim on PCOL</h3>
              <p>Use the guidance below to complete your claim at <a href="https://www.possessionclaim.gov.uk" target="_blank" rel="noopener noreferrer">possessionclaim.gov.uk</a>. Estatel AI does not submit the claim — you do this manually, then confirm below.</p>

              {pcolGuidance && (
                <div data-testid="sc-pcol-guidance">
                  <div className="sc-guidance-fields">
                    {pcolGuidance.guidance_fields.map((f, i) => (
                      <div key={i} className="sc-guidance-field">
                        <span className="sc-gf-label">{f.field}</span>
                        <span className="sc-gf-value">{f.value}</span>
                        <span className="sc-gf-instruction">{f.instruction}</span>
                      </div>
                    ))}
                  </div>
                  <div className="sc-pcol-notes">
                    {pcolGuidance.important_notes.map((n, i) => <p key={i}>{n}</p>)}
                  </div>
                </div>
              )}

              <div className="sc-form-row" style={{marginTop: '1rem'}}>
                <div className="sc-form-group">
                  <label>PCOL Claim Number</label>
                  <input type="text" value={pcolClaimNum} onChange={e => setPcolClaimNum(e.target.value)} placeholder="Enter after PCOL submission" data-testid="sc-pcol-num" />
                  <button className="sc-btn sc-btn-sm" onClick={handleSetPcol} data-testid="sc-btn-save-pcol">Save</button>
                </div>
                <div className="sc-form-group">
                  <label>Court Filing Deadline</label>
                  <UKDateInput value={filingDeadline} onChange={e => setFilingDeadline(e.target.value)} data-testid="sc-filing-deadline" />
                  <button className="sc-btn sc-btn-sm" onClick={handleSetDeadline} data-testid="sc-btn-save-deadline">Save</button>
                </div>
              </div>

              {!caseData.pcol_claim_submitted ? (
                <button className="sc-btn sc-btn-primary" onClick={handleMarkClaimSubmitted} disabled={processing} data-testid="sc-btn-mark-submitted" style={{marginTop: '1rem'}}>
                  Claim Submitted on PCOL
                </button>
              ) : (
                <div className="sc-phase-done" data-testid="sc-claim-submitted-badge">
                  Claim submitted on PCOL
                </div>
              )}
            </div>

            {/* PHASE 2: Upload Issued Documents (only after claim submitted) */}
            {caseData.pcol_claim_submitted && (
              <div className="sc-subsection" data-testid="sc-phase-2">
                <h3>Phase 2 — Upload Issued Documents from PCOL</h3>
                <p>Download N5, N119, and the PCOL rent statement from PCOL, then upload each below.</p>

                <div className="sc-upload-grid">
                  <div className="sc-upload-card" data-testid="sc-upload-n5">
                    <h4>N5 — Claim Form (issued by PCOL)</h4>
                    {(caseData.uploaded_labels || []).includes('n5_issued') ? (
                      <div className="sc-phase-done">Uploaded</div>
                    ) : (
                      <>
                        <input type="file" accept=".pdf" onChange={e => setUploadN5File(e.target.files[0])} data-testid="sc-upload-n5-file" />
                        <button className="sc-btn sc-btn-primary" onClick={() => handleUploadSpecific('n5_issued', uploadN5File)} disabled={processing} data-testid="sc-btn-upload-n5">Upload N5</button>
                      </>
                    )}
                  </div>

                  <div className="sc-upload-card" data-testid="sc-upload-n119">
                    <h4>N119 — Particulars of Claim (issued by PCOL)</h4>
                    {(caseData.uploaded_labels || []).includes('n119_issued') ? (
                      <div className="sc-phase-done">Uploaded</div>
                    ) : (
                      <>
                        <input type="file" accept=".pdf" onChange={e => setUploadN119File(e.target.files[0])} data-testid="sc-upload-n119-file" />
                        <button className="sc-btn sc-btn-primary" onClick={() => handleUploadSpecific('n119_issued', uploadN119File)} disabled={processing} data-testid="sc-btn-upload-n119">Upload N119</button>
                      </>
                    )}
                  </div>

                  <div className="sc-upload-card" data-testid="sc-upload-rent-stmt">
                    <h4>PCOL Rent Statement</h4>
                    {(caseData.uploaded_labels || []).includes('pcol_rent_statement') ? (
                      <div className="sc-phase-done">Uploaded</div>
                    ) : (
                      <>
                        <input type="file" accept=".pdf" onChange={e => setUploadRentStmtFile(e.target.files[0])} data-testid="sc-upload-rent-stmt-file" />
                        <button className="sc-btn sc-btn-primary" onClick={() => handleUploadSpecific('pcol_rent_statement', uploadRentStmtFile)} disabled={processing} data-testid="sc-btn-upload-rent-stmt">Upload Rent Statement</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* PHASE 3: Rent Schedule + Tenancy Agreement */}
            {caseData.pcol_claim_submitted && (
              <div className="sc-subsection" data-testid="sc-phase-3">
                <h3>Phase 3 — Rent Schedule and Supporting Documents</h3>
                <p>Generate the landlord's rent schedule from Estatel AI arrears records, and upload the tenancy agreement.</p>

                <div className="sc-upload-grid">
                  <div className="sc-upload-card" data-testid="sc-rent-schedule-card">
                    <h4>Rent Schedule (4-column layout)</h4>
                    {(caseData.documents || []).some(d => d.doc_type === 'rent_schedule') ? (
                      <div className="sc-phase-done">Generated</div>
                    ) : (
                      <button className="sc-btn sc-btn-primary" onClick={handleGenerateRentSchedule} disabled={processing} data-testid="sc-btn-gen-rent-schedule">
                        Generate Rent Schedule
                      </button>
                    )}
                  </div>

                  <div className="sc-upload-card" data-testid="sc-upload-tenancy">
                    <h4>Tenancy Agreement</h4>
                    {/* TASK 4 — use the agreement stored on the Property
                        Detail Documents tab. No upload here. */}
                    {(caseData.property_tenancy_agreement_on_file ||
                      (caseData.uploaded_labels || []).includes('tenancy_agreement')) ? (
                      <div className="sc-phase-done" data-testid="sc-tenancy-on-file">
                        On file ·{' '}
                        {caseData.property_id ? (
                          <a href={`${BACKEND_URL}/api/properties/${caseData.property_id}/tenancy-agreement`}
                             target="_blank" rel="noopener noreferrer"
                             data-testid="sc-view-property-tenancy-agreement"
                             style={{ color: '#0073EA', fontWeight: 600 }}>
                            View Agreement
                          </a>
                        ) : 'View Agreement'}
                      </div>
                    ) : (
                      <div data-testid="sc-tenancy-missing-warning"
                           style={{ background: '#fff7ed', border: '1px solid #fdba74',
                                    color: '#9a3412', padding: '0.65rem 0.85rem',
                                    borderRadius: 6, fontSize: '0.88rem' }}>
                        No tenancy agreement on file for this property. Please
                        upload it in the{' '}
                        {caseData.property_id ? (
                          <Link to={`/properties/${caseData.property_id}`}
                                style={{ color: '#9a3412', fontWeight: 600,
                                         textDecoration: 'underline' }}
                                data-testid="sc-tenancy-go-property">
                            Property Detail Documents tab
                          </Link>
                        ) : 'Property Detail Documents tab'}
                        {' '}before proceeding.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* PHASE 4: Bundle Assembly (only when all uploads done) */}
            {caseData.pcol_claim_submitted && (() => {
              const labels = caseData.uploaded_labels || [];
              const hasN5 = labels.includes('n5_issued');
              const hasN119 = labels.includes('n119_issued');
              const hasRentStmt = labels.includes('pcol_rent_statement');
              const hasRentSchedule = (caseData.documents || []).some(d => d.doc_type === 'rent_schedule');
              const allReady = hasN5 && hasN119 && hasRentStmt && hasRentSchedule;
              return (
                <div className="sc-subsection" data-testid="sc-phase-4">
                  <h3>Phase 4 — Bundle Assembly</h3>
                  {!allReady ? (
                    <div className="sc-bundle-checklist">
                      <p>Complete all uploads before bundle assembly:</p>
                      <ul>
                        <li className={hasN5 ? 'sc-check-done' : 'sc-check-pending'}>N5 (issued by PCOL) {hasN5 ? '— uploaded' : '— pending'}</li>
                        <li className={hasN119 ? 'sc-check-done' : 'sc-check-pending'}>N119 (issued by PCOL) {hasN119 ? '— uploaded' : '— pending'}</li>
                        <li className={hasRentStmt ? 'sc-check-done' : 'sc-check-pending'}>PCOL Rent Statement {hasRentStmt ? '— uploaded' : '— pending'}</li>
                        <li className={hasRentSchedule ? 'sc-check-done' : 'sc-check-pending'}>Rent Schedule {hasRentSchedule ? '— generated' : '— pending'}</li>
                      </ul>
                    </div>
                  ) : (
                    <div className="sc-bundle-ready" data-testid="sc-bundle-ready">
                      <p><strong>All documents ready.</strong> Estatel AI will assemble the following bundles:</p>
                      <div className="sc-bundle-info">
                        <div>
                          <h4>Bundle 1 — Court</h4>
                          <ol>
                            <li>Court covering letter</li>
                            <li>Court N215 (Certificate of Service)</li>
                            <li>N5 (Claim Form)</li>
                            <li>N119 (Particulars of Claim)</li>
                            <li>PCOL Rent Statement</li>
                            <li>Agent Rent Schedule</li>
                            <li>Tenancy Agreement</li>
                            <li>Section 8 Notice</li>
                            <li>Section 8 Service N215</li>
                          </ol>
                        </div>
                        <div>
                          <h4>Bundle 2 — Tenant</h4>
                          <p>Bundle 1 minus the first two documents (starting from N5).</p>
                        </div>
                      </div>
                      <p className="sc-bundle-note">Sign both N215s and add service dates before posting.</p>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Move to Stage 7 */}
            <div className="sc-subsection">
              <button className="sc-btn sc-btn-primary" onClick={async () => {
                if (!hearingDate) return window.alert('Enter a hearing date first');
                await handleRecordHearing();
              }} disabled={processing} data-testid="sc-btn-to-stage7">
                Record Hearing Date and Move to Stage 7
              </button>
              <div className="sc-form-group" style={{marginTop: '0.5rem'}}>
                <label>Hearing Date</label>
                <UKDateInput value={hearingDate} onChange={e => setHearingDate(e.target.value)} data-testid="sc-hearing-date-input" />
              </div>
            </div>
          </div>
        )}

        {/* STAGE 6 PAPER-BASED ROUTE (Step 48 FIX 4): non-rent grounds only.
            Generates N5, N119, N215×2, assembles a single signed-ready bundle. */}
        {stage === 6 && caseData.proceeding_route === 'paper_based' && (() => {
          // FIX 5 — persistent amber banner whenever notice period has not
          // yet expired. Stays visible through the entire Stage 6 prep.
          const npe = caseData.notice_period_end ? new Date(caseData.notice_period_end) : null;
          const expired = npe && npe.getTime() <= Date.now();
          const days = npe ? Math.max(0, Math.ceil((npe.getTime() - Date.now()) / 86400000)) : null;
          const fmt = (d) => d ? new Date(d).toLocaleDateString('en-GB',
            { day: 'numeric', month: 'long', year: 'numeric' }) : '';
          const banner = (npe && !expired) ? (
            <div data-testid="advance-prep-banner"
                 style={{ background: '#fff7ed', border: '1px solid #fdba74',
                          color: '#9a3412', padding: '0.85rem 1.1rem',
                          borderRadius: 8, marginBottom: '1rem',
                          fontSize: '0.92rem' }}>
              <strong>Notice period still running.</strong> The notice period
              for this tenancy has not yet expired. Notice period ends on{' '}
              <strong>{fmt(caseData.notice_period_end)}</strong> —{' '}
              <strong>{days} day{days === 1 ? '' : 's'} remaining</strong>.
              Court proceedings should not be issued until this date.
              You are preparing the bundle in advance.
            </div>
          ) : null;
          return banner;
        })()}
        {stage === 6 && caseData.proceeding_route === 'paper_based' && (() => {
          const docs = caseData.documents || [];
          const docTypes = new Set(docs.map(d => d.doc_type));
          const labels = caseData.uploaded_labels || [];
          const tenancyAgreementUploaded = labels.includes('tenancy_agreement') || !!caseData.property_tenancy_agreement_on_file;
          const courtAddressSet = !!(caseData.court_name && caseData.court_address_line1 && caseData.court_postcode);
          const bundleGenerated = docTypes.has('court_bundle');
          const bundlePosted = !!caseData.n215_bundle_posted_date;
          const feePaid = !!caseData.court_fee_paid_date;
          const claimNum = caseData.pcol_claim_number;
          const hearingOnRecord = !!caseData.hearing_date;
          const claimBackfilled = !!caseData.claim_number_backfilled_at;
          const indiv = (v) => (
            <tr><td style={{ padding: '0.35rem 0.75rem 0.35rem 0', color: '#475569', fontSize: '0.9rem' }}>{v[0]}</td>
                <td style={{ padding: '0.35rem 0', fontSize: '0.9rem' }}>{v[1]}</td></tr>
          );
          return (
            <div className="sc-section" data-testid="sc-stage-6-paper">
              <h2>Stage 6 — Start Court Proceedings (Paper-based)</h2>
              <div
                data-testid="sc-paper-disclaimer"
                style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', padding: '0.8rem 1rem', borderRadius: 6, marginBottom: '1rem', fontSize: '0.92rem' }}>
                Estatel AI populates documents based on information you have provided. This is not legal advice. If you are unsure of your legal position please consult a solicitor.
              </div>

              {/* STEP 1 — Court address (structured) */}
              <div className="sc-subsection">
                <h3>1. Court details</h3>
                <p style={{ color: '#475569', fontSize: '0.92rem' }}>
                  Use the HMCTS court finder with the let property postcode, then enter the court details below.{' '}
                  <a href="https://www.find-court-tribunal.service.gov.uk/services/money/housing/nearest/search-by-postcode"
                     target="_blank" rel="noopener noreferrer"
                     data-testid="sc-court-finder-link"
                     style={{ color: '#0073EA', fontWeight: 500 }}>
                    Open court finder
                  </a>
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '0.75rem', marginTop: '0.5rem' }}>
                  {[
                    ['Court name *', 'court_name', 'sc-court-name'],
                    ['Address line 1 *', 'court_address_line1', 'sc-court-addr1'],
                    ['Address line 2', 'court_address_line2', 'sc-court-addr2'],
                    ['City *', 'court_city', 'sc-court-city'],
                    ['County', 'court_county', 'sc-court-county'],
                    ['Postcode *', 'court_postcode', 'sc-court-postcode'],
                  ].map(([label, k, tid]) => (
                    <div key={k}>
                      <label style={{ display: 'block', fontSize: '0.82rem', color: '#475569', marginBottom: 2 }}>{label}</label>
                      <input
                        type="text"
                        value={courtForm[k] || ''}
                        onChange={e => setCourtForm({ ...courtForm, [k]: e.target.value })}
                        data-testid={tid}
                        style={{ width: '100%', padding: '0.5rem', borderRadius: 4, border: '1px solid #cbd5e1', background: '#fff', color: '#0f172a', fontSize: '0.9rem' }}
                      />
                    </div>
                  ))}
                </div>
                {/* Tenancy start date — manual override drives N119 Section 3 */}
                <div style={{ marginTop: '0.85rem', padding: '0.75rem', border: '1px dashed #cbd5e1', borderRadius: 6, background: '#f8fafc' }}>
                  <label style={{ display: 'block', fontSize: '0.82rem', color: '#475569', marginBottom: 2 }}>
                    Tenancy start date *
                  </label>
                  <input
                    type="date"
                    value={courtForm.tenancy_start_override || ''}
                    onChange={e => setCourtForm({ ...courtForm, tenancy_start_override: e.target.value })}
                    data-testid="sc-tenancy-start-override"
                    style={{ padding: '0.5rem', borderRadius: 4, border: '1px solid #cbd5e1', background: '#fff', color: '#0f172a', fontSize: '0.9rem' }}
                  />
                  <p style={{ margin: '0.4rem 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                    Confirm the date the tenancy began. This populates N119 Section 3. Pre-filled
                    from the tenancy record &mdash; edit if it is wrong.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleSavePaperCourtAddress}
                  disabled={processing}
                  data-testid="sc-btn-save-paper-court"
                  style={{ marginTop: '0.75rem', background: '#0073EA', color: '#fff', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 6, cursor: 'pointer', fontWeight: 500 }}>
                  {courtAddressSet ? 'Update Court Details' : 'Save Court Details'}
                </button>
              </div>

              {/* TASK 4 — Tenancy agreement reference: use the property's
                  stored agreement instead of forcing a separate upload here. */}
              {caseData.property_tenancy_agreement_on_file ? (
                <div className="sc-subsection">
                  <h3>Tenancy agreement — on file</h3>
                  <p style={{ color: '#475569', fontSize: '0.9rem', margin: '0.25rem 0 0.5rem' }}>
                    Using the signed agreement stored on the Property record.
                  </p>
                  {caseData.property_id && (
                    <a href={`${BACKEND_URL}/api/properties/${caseData.property_id}/tenancy-agreement`}
                       target="_blank" rel="noopener noreferrer"
                       data-testid="sc-paper-view-property-tenancy-agreement"
                       style={{ color: '#0073EA', fontWeight: 600 }}>
                      View Agreement
                    </a>
                  )}
                </div>
              ) : (
                <div className="sc-subsection">
                  <h3>Tenancy agreement — required for bundle</h3>
                  <div data-testid="sc-paper-tenancy-missing-warning"
                       style={{ background: '#fff7ed', border: '1px solid #fdba74',
                                color: '#9a3412', padding: '0.7rem 0.95rem',
                                borderRadius: 6, fontSize: '0.9rem',
                                display: 'flex', justifyContent: 'space-between',
                                alignItems: 'center', gap: '0.75rem',
                                flexWrap: 'wrap' }}>
                    <span>
                      No tenancy agreement on file for this property. Upload it
                      now to include it in the court bundle.
                    </span>
                    <button type="button"
                            onClick={() => {
                              setInlineTenancyFile(null);
                              setInlineTenancyError(null);
                              setShowTenancyUploadModal(true);
                            }}
                            data-testid="sc-paper-tenancy-upload-btn"
                            style={{ background: '#0073EA', color: '#fff',
                                     border: 'none', padding: '0.45rem 1rem',
                                     borderRadius: 6, cursor: 'pointer',
                                     fontWeight: 500, fontSize: '0.9rem' }}>
                      Upload Tenancy Agreement
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 2 — Generate bundle */}
              <div className="sc-subsection">
                <h3>2. Generate the court bundle</h3>
                <p style={{ color: '#475569', fontSize: '0.92rem' }}>
                  Estatel AI will fill the official HMCTS N5, N119, two N215 certificates, and generate court and tenant covering letters, then assemble them in the order: court letter → N215 (bundle service) → tenant letter → N5 → N119 → tenancy agreement → Form 3A → N215 (notice service).
                </p>
                {/* Step 54 FIX 14 — page 6 N5 hand-completion alert */}
                <div data-testid="sc-n5-page6-alert"
                     style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412',
                              padding: '0.7rem 0.9rem', borderRadius: 6, marginBottom: '0.6rem',
                              fontSize: '0.9rem' }}>
                  <strong>Before posting your N5 complete page 6 by hand.</strong> You have two options:
                  (a) request that the court emails you payment details — write your email address in the
                  box on page 6, or (b) arrange payment by debit or credit card by asking the court to call
                  you — write your phone number in the box on page 6. Do not post without completing page 6.
                </div>
                {/* Step 54 FIX 19 — N119 section 8.1 review alert */}
                <div data-testid="sc-n119-section81-alert"
                     style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412',
                              padding: '0.7rem 0.9rem', borderRadius: 6, marginBottom: '0.6rem',
                              fontSize: '0.9rem' }}>
                  <strong>Before printing review N119 section 8.1.</strong> If you have additional financial
                  information relevant to your circumstances add it by hand in that section before posting.
                </div>
                <div data-testid="sc-n119-sot-alert"
                     style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412',
                              padding: '0.7rem 0.9rem', borderRadius: 6, marginBottom: '0.6rem',
                              fontSize: '0.9rem' }}>
                  <strong>N119 — handwrite two dates before posting.</strong> The official HMCTS N119 template
                  has no fillable box for (a) the date your Section 8 notice was served on page 4 (Q6), or
                  (b) the statement-of-truth date on page 9. Both must be written in by hand alongside your
                  signature.
                </div>
                {/* N215 bundle-service completion alert */}
                <div data-testid="sc-n215-bundle-service-alert"
                     style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412',
                              padding: '0.7rem 0.9rem', borderRadius: 6, marginBottom: '0.6rem',
                              fontSize: '0.9rem' }}>
                  <strong>N215 (bundle service) — complete by hand on the day you post.</strong> Estatel AI
                  cannot know in advance the exact date the bundle leaves your hand. Before posting, write
                  in (a) the day-of-service date in box "Day served", (b) the deemed-service date
                  (first-class post = +2 business days), and tick the matching method in Q5. Then sign and
                  date the Statement of Truth on page 4.
                </div>
                {/* ITEM 2 — Stage 6 service-method confirmation. Drives N215
                    notice service Q5 ticks and the deemed-service date. */}
                <div data-testid="sc-notice-service-method-block"
                     style={{ marginBottom: '0.8rem', padding: '0.85rem 1rem',
                              border: '1px solid #cbd5e1', borderRadius: 6,
                              background: '#f8fafc' }}>
                  <h4 style={{ margin: '0 0 0.6rem', fontSize: '0.95rem', color: '#0f172a' }}>
                    How was the Section 8 notice served on the tenant?
                  </h4>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                    <input
                      type="radio" name="sc-notice-service-method"
                      value="first_class_post"
                      checked={courtForm.notice_service_method === 'first_class_post'}
                      onChange={() => setCourtForm({ ...courtForm, notice_service_method: 'first_class_post' })}
                      data-testid="sc-notice-service-method-fcp"
                    />
                    By first class post (default)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                    <input
                      type="radio" name="sc-notice-service-method"
                      value="by_hand"
                      checked={courtForm.notice_service_method === 'by_hand'}
                      onChange={() => setCourtForm({ ...courtForm, notice_service_method: 'by_hand' })}
                      data-testid="sc-notice-service-method-hand"
                    />
                    By hand (personal delivery)
                  </label>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.78rem', color: '#64748b' }}>
                    Saved to the case when you press Save Court Details. Required before
                    the bundle can be assembled.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleGeneratePaperBundle}
                  disabled={processing || !courtAddressSet || !courtForm.notice_service_method || caseData.property_landlord_attached === false}
                  data-testid="sc-btn-generate-paper-bundle"
                  style={{
                    background: (!courtAddressSet || !courtForm.notice_service_method || caseData.property_landlord_attached === false) ? '#94a3b8' : '#0073EA',
                    color: '#fff', border: 'none',
                    padding: '0.7rem 1.2rem', borderRadius: 6, fontWeight: 500,
                    cursor: (!courtAddressSet || !courtForm.notice_service_method || caseData.property_landlord_attached === false) ? 'not-allowed' : 'pointer',
                  }}>
                  {processing ? 'Generating…' : 'Start Court Proceedings'}
                </button>
                {/* 2026-02 — Pre-flight: surface "no landlord attached"
                    BEFORE the agent clicks Generate. Previously the
                    button enabled, the agent clicked, and the
                    paper-bundle endpoint returned HTTP 400 with the
                    detail "Landlord record missing — attach a landlord
                    to the property before generating the bundle." */}
                {caseData.property_landlord_attached === false && (
                  <div
                    data-testid="sc-paper-landlord-missing-warning"
                    style={{
                      marginTop: '0.5rem',
                      background: '#fef2f2',
                      border: '1px solid #fca5a5',
                      color: '#991b1b',
                      padding: '0.6rem 0.85rem',
                      borderRadius: 6,
                      fontSize: '0.88rem',
                    }}
                  >
                    <strong>No landlord attached to this property.</strong>{' '}
                    A landlord record is required to populate the court bundle.{' '}
                    {caseData.property_id && (
                      <a
                        href={`/property/${caseData.property_id}`}
                        style={{ color: '#0073EA', fontWeight: 600 }}
                        data-testid="sc-paper-landlord-missing-fix-link"
                      >
                        Open the property to attach a landlord →
                      </a>
                    )}
                  </div>
                )}
                {!courtAddressSet && (
                  <p style={{ color: '#9a3412', fontSize: '0.85rem', marginTop: '0.4rem' }}>
                    Save the court details before generating the bundle.
                  </p>
                )}
                {courtAddressSet && !courtForm.notice_service_method && (
                  <p data-testid="sc-notice-service-method-required"
                     style={{ color: '#9a3412', fontSize: '0.85rem', marginTop: '0.4rem' }}>
                    Confirm the method of service for the Section 8 notice (above) before generating the bundle.
                  </p>
                )}
                {bundleGenerated && (
                  <div style={{ marginTop: '0.75rem' }} data-testid="sc-paper-bundle-ready">
                    <a
                      href={`${BACKEND_URL}/api/section8/document-pdf/${case_id}/court_bundle`}
                      target="_blank" rel="noopener noreferrer"
                      data-testid="sc-paper-bundle-download"
                      style={{ color: '#0073EA', fontWeight: 500 }}>
                      Download complete bundle (PDF)
                    </a>
                    <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }} data-testid="sc-individual-docs">
                      {[
                        ['court_covering_letter', 'Court covering letter'],
                        ['n215_bundle_service', 'N215 — Bundle service'],
                        ['tenant_covering_letter', 'Tenant covering letter'],
                        ['n5_paper', 'N5'],
                        ['n119_paper', 'N119'],
                        ['n215_notice_service', 'N215 — Notice service'],
                      ].filter(([t]) => docTypes.has(t)).map(([t, label]) => {
                        const chip = (
                          <a key={`a-${t}`} href={`${BACKEND_URL}/api/section8/document-pdf/${case_id}/${t}`}
                             target="_blank" rel="noopener noreferrer"
                             data-testid={`sc-doc-link-${t}`}
                             style={{ fontSize: '0.85rem', color: '#0073EA', padding: '0.25rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: 4 }}>
                            {label}
                          </a>
                        );
                        // FIX 2 (revised) — visually attach a small grey instructional note
                        // directly below the N215 Bundle service chip only. Note is part of
                        // the chip column, not a separate list item.
                        if (t === 'n215_bundle_service') {
                          return (
                            <div key={t} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.2rem' }}>
                              {chip}
                              <span data-testid="sc-n215-bundle-date-inline-alert"
                                    style={{ fontSize: '0.72rem', color: '#64748b', fontStyle: 'italic' }}>
                                Date this N215 before posting.
                              </span>
                            </div>
                          );
                        }
                        return chip;
                      })}
                      {/* Section 8 notice — pick first available variant */}
                      {(() => {
                        const s8Type = ['section8_notice_signed', 'section8_notice', 'form_3a'].find(t => docTypes.has(t));
                        if (!s8Type) return null;
                        return (
                          <a key="s8-notice" href={`${BACKEND_URL}/api/section8/document-pdf/${case_id}/${s8Type}`}
                             target="_blank" rel="noopener noreferrer"
                             data-testid="sc-doc-link-section8-notice"
                             style={{ fontSize: '0.85rem', color: '#0073EA', padding: '0.25rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: 4 }}>
                            Section 8 notice (Form 3A)
                          </a>
                        );
                      })()}
                      {/* Tenancy agreement — match by doc_type or doc_label,
                          or fall back to the property's stored agreement (TASK 4). */}
                      {(() => {
                        const ta = (caseData.documents || []).find(d =>
                          d.doc_type === 'tenancy_agreement' ||
                          (d.doc_label || '').toLowerCase().includes('tenancy agreement') ||
                          (d.doc_label || '').toLowerCase() === 'tenancy_agreement'
                        );
                        if (ta) {
                          return (
                            <a key="tenancy-agreement"
                               href={`${BACKEND_URL}/api/section8/document-pdf/${case_id}/tenancy_agreement`}
                               target="_blank" rel="noopener noreferrer"
                               data-testid="sc-doc-link-tenancy-agreement"
                               style={{ fontSize: '0.85rem', color: '#0073EA', padding: '0.25rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: 4 }}>
                              Tenancy agreement
                            </a>
                          );
                        }
                        if (caseData.property_tenancy_agreement_on_file && caseData.property_id) {
                          return (
                            <a key="tenancy-agreement"
                               href={`${BACKEND_URL}/api/properties/${caseData.property_id}/tenancy-agreement`}
                               target="_blank" rel="noopener noreferrer"
                               data-testid="sc-doc-link-tenancy-agreement"
                               style={{ fontSize: '0.85rem', color: '#0073EA', padding: '0.25rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: 4 }}>
                              Tenancy agreement
                            </a>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {/* STEP 3 — Confirm bundle posted */}
              {bundleGenerated && (
                <div className="sc-subsection">
                  <h3>3. Confirm bundle posted</h3>
                  {bundlePosted ? (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <div data-testid="sc-bundle-posted-badge"
                           style={{ background: '#10b981', color: '#fff', fontWeight: 600, padding: '0.55rem 1rem', borderRadius: 999, fontSize: '0.92rem' }}>
                        Posted on {String(caseData.n215_bundle_posted_date).slice(0, 10)}
                      </div>
                      <button type="button" onClick={handleUndoBundlePosted} disabled={processing}
                              data-testid="sc-btn-undo-bundle-posted"
                              style={{ background: 'none', border: 'none', color: '#0073EA', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.92rem' }}>
                        {processing ? 'Clearing…' : 'Undo'}
                      </button>
                    </div>
                  ) : (
                    <button type="button" onClick={handleMarkBundlePosted} disabled={processing}
                            data-testid="sc-btn-mark-bundle-posted"
                            style={{ background: '#0073EA', color: '#fff', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 6, fontWeight: 500 }}>
                      {processing ? 'Saving…' : 'Mark Bundle as Posted'}
                    </button>
                  )}
                </div>
              )}

              {/* STEP 4 — Court fee payment */}
              {bundlePosted && (
                <div className="sc-subsection">
                  <h3>4. Court fee payment</h3>
                  {feePaid ? (
                    <div data-testid="sc-fee-paid-badge"
                         style={{ display: 'inline-block', background: '#10b981', color: '#fff', padding: '0.45rem 0.9rem', borderRadius: 999, fontWeight: 600, fontSize: '0.92rem' }}>
                      Fee paid on {String(caseData.court_fee_paid_date).slice(0, 10)}
                    </div>
                  ) : (
                    <>
                      <p style={{ color: '#475569', fontSize: '0.92rem' }}>
                        <strong>Awaiting court contact to arrange fee payment.</strong> The court will call you to take payment. When that happens, record the date below.
                      </p>
                      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <UKDateInput value={feePaidDate} onChange={e => setFeePaidDate(e.target.value)} data-testid="sc-fee-paid-date" />
                        <button type="button" onClick={handleMarkFeePaid} disabled={processing}
                                data-testid="sc-btn-mark-fee-paid"
                                style={{ background: '#0073EA', color: '#fff', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 6, fontWeight: 500 }}>
                          {processing ? 'Saving…' : 'Mark Fee Paid'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* STEP 5 — Upload Notice of Hearing */}
              {bundlePosted && !hearingOnRecord && (
                <div className="sc-subsection">
                  <h3>5. Upload Notice of Hearing</h3>
                  <p style={{ color: '#475569', fontSize: '0.92rem' }}>
                    The court will post you a Notice of Hearing containing the claim number and hearing date. Upload it and Estatel AI will extract both for you to confirm.
                  </p>
                  <input type="file" accept=".pdf" onChange={e => setNohFile(e.target.files[0])} data-testid="sc-noh-file" />
                  <button type="button" onClick={handleUploadNoH} disabled={processing || !nohFile}
                          data-testid="sc-btn-upload-noh"
                          style={{ background: '#0073EA', color: '#fff', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 6, marginLeft: '0.5rem' }}>
                    {processing ? 'Extracting…' : 'Upload & Extract'}
                  </button>
                  {nohExtracted && (
                    <div data-testid="sc-noh-review" style={{ marginTop: '0.75rem', padding: '0.75rem', border: '1px dashed #cbd5e1', borderRadius: 6, background: '#f8fafc' }}>
                      <p style={{ margin: 0, fontSize: '0.9rem', color: '#475569' }}>Review the extracted details, correct if needed, then confirm to advance to Stage 7.</p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.5rem' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.82rem', color: '#475569', marginBottom: 2 }}>Claim number</label>
                          <input type="text" value={nohClaimNum} onChange={e => setNohClaimNum(e.target.value)}
                                 data-testid="sc-noh-claim"
                                 style={{ width: '100%', padding: '0.5rem', borderRadius: 4, border: '1px solid #cbd5e1', background: '#fff', color: '#0f172a' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.82rem', color: '#475569', marginBottom: 2 }}>Hearing date</label>
                          <UKDateInput value={nohHearingDate} onChange={e => setNohHearingDate(e.target.value)} data-testid="sc-noh-hearing-date" />
                        </div>
                      </div>
                      <button type="button" onClick={handleConfirmNoH} disabled={processing}
                              data-testid="sc-btn-confirm-noh"
                              style={{ background: '#0073EA', color: '#fff', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 6, marginTop: '0.75rem', fontWeight: 500 }}>
                        {processing ? 'Saving…' : 'Confirm & Advance to Stage 7'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* STEP 6 — Claim number backfill */}
              {claimNum && !claimBackfilled && (
                <div className="sc-subsection">
                  <h3>6. Update N215 documents with claim number</h3>
                  <p style={{ color: '#475569', fontSize: '0.92rem' }}>
                    Your claim number <strong>{claimNum}</strong> has been received. Would you like to update your N215 documents to include it?
                  </p>
                  <button type="button" onClick={handleBackfillClaim} disabled={processing}
                          data-testid="sc-btn-backfill-claim"
                          style={{ background: '#0073EA', color: '#fff', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 6, fontWeight: 500 }}>
                    {processing ? 'Regenerating…' : 'Regenerate N215s with claim number'}
                  </button>
                </div>
              )}
              {claimBackfilled && (
                <div className="sc-subsection" data-testid="sc-claim-backfilled-banner">
                  <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', padding: '0.7rem 1rem', borderRadius: 6, fontSize: '0.92rem' }}>
                    Updated N215 documents have been generated with claim number <strong>{claimNum}</strong>. Print, re-sign, and retain for your records. You do not need to resubmit these unless the court specifically asks.
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* STAGE 7: Hearing Preparation */}
        {stage === 7 && (
          <div className="sc-section" data-testid="sc-stage-7">
            <h2>Stage 7 — Hearing Preparation</h2>
            {caseData.hearing_date && (
              <div className={`sc-countdown ${caseData.hearing_alert === 'red' ? 'sc-alert-red' : caseData.hearing_alert === 'amber' ? 'sc-alert-amber' : ''}`} data-testid="sc-hearing-countdown">
                Hearing date: <strong>{caseData.hearing_date}</strong> — <strong>{caseData.hearing_countdown} days</strong>
              </div>
            )}

            <div className="sc-info-banner" data-testid="sc-solicitor-prompt">
              You have an upcoming court hearing. If you do not have legal representation, consider instructing a solicitor.
            </div>

            <div className="sc-actions">
              <button className="sc-btn sc-btn-primary" onClick={handleGenerateSummary} disabled={processing} data-testid="sc-btn-gen-summary">
                {processing ? 'Generating...' : 'Generate Case Summary'}
              </button>
              <button
                className="sc-btn sc-btn-secondary"
                style={{ background: '#0073EA', color: '#fff', border: 'none' }}
                onClick={handleAuditExport}
                disabled={processing}
                data-testid="sc-btn-audit-7">
                Export Full Case Audit
              </button>
            </div>

            <div className="sc-subsection" style={{marginTop: '2rem'}}>
              <h3>Record Outcome</h3>
              <p>After the hearing, record the court's decision below.</p>
              <div className="sc-form-group">
                <label>Outcome</label>
                <select value={outcome} onChange={e => setOutcome(e.target.value)} data-testid="sc-outcome-select">
                  <option value="possession_granted">Possession Granted</option>
                  <option value="suspended">Suspended Possession Order</option>
                  <option value="dismissed">Claim Dismissed</option>
                  <option value="adjourned">Adjourned</option>
                </select>
              </div>
              {outcome === 'possession_granted' && (
                <div className="sc-form-group">
                  <label>Possession Date</label>
                  <UKDateInput value={possessionDate} onChange={e => setPossessionDate(e.target.value)} data-testid="sc-possession-date" />
                </div>
              )}
              {outcome === 'suspended' && (
                <div className="sc-form-group">
                  <label>Conditions</label>
                  <textarea value={conditions} onChange={e => setConditions(e.target.value)} rows={3} placeholder="Enter the conditions of the suspended order" data-testid="sc-conditions" />
                </div>
              )}
              {outcome === 'dismissed' && (
                <div className="sc-form-group">
                  <label>Reason for Dismissal</label>
                  <textarea value={dismissReason} onChange={e => setDismissReason(e.target.value)} rows={3} data-testid="sc-dismiss-reason" />
                </div>
              )}
              {outcome === 'adjourned' && (
                <div className="sc-form-group">
                  <label>New Hearing Date</label>
                  <UKDateInput value={newHearingDate} onChange={e => setNewHearingDate(e.target.value)} data-testid="sc-new-hearing-date" />
                </div>
              )}
              <button className="sc-btn sc-btn-primary" onClick={handleRecordOutcome} disabled={processing} data-testid="sc-btn-record-outcome">
                {processing ? 'Recording...' : 'Record Outcome'}
              </button>
            </div>
          </div>
        )}

        {/* STAGE 8: Outcome Recorded */}
        {stage === 8 && (
          <div className="sc-section" data-testid="sc-stage-8">
            <h2>Stage 8 — Outcome Recorded</h2>
            <div className="sc-outcome-card" data-testid="sc-outcome-card">
              <h3>Outcome: <span className="sc-outcome-label">{caseData.outcome?.replace('_', ' ')}</span></h3>
              {caseData.outcome === 'possession_granted' && (
                <>
                  {caseData.possession_date ? (
                    <p>Possession date: <strong>{caseData.possession_date}</strong></p>
                  ) : (
                    <div className="sc-form-group">
                      <p>Record the date you take possession of the property:</p>
                      <UKDateInput value={finalPossDate} onChange={e => setFinalPossDate(e.target.value)} data-testid="sc-final-poss-date" />
                      <button className="sc-btn sc-btn-primary" onClick={handleRecordFinalPossession} disabled={processing} data-testid="sc-btn-final-poss">
                        Record Possession
                      </button>
                    </div>
                  )}
                </>
              )}
              {caseData.outcome === 'suspended' && (
                <div className="sc-suspended-alert" data-testid="sc-suspended-alert">
                  <strong>Monitoring Alert:</strong> Suspended Possession Order in effect.
                  {caseData.suspended_conditions && <p>Conditions: {caseData.suspended_conditions}</p>}
                </div>
              )}
              {caseData.outcome === 'dismissed' && (
                <p>Reason: {caseData.dismissal_reason || 'No reason recorded'}</p>
              )}
            </div>
            {/* 2026-02 FIX 2 — Archive option is permitted ONLY when an
                outcome has been recorded (i.e. case is at Stage 8 with
                outcome). Cases are legal audit records: server-side
                `is_deletable=false` so they are NEVER hard-deleted. */}
            {caseData.outcome && (
              <div style={{ marginTop: 16, padding: '0.85rem 1rem',
                            background: '#fafafa', border: '1px solid #e5e7eb',
                            borderRadius: 8, display: 'flex',
                            alignItems: 'center', justifyContent: 'space-between',
                            gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 600, color: '#0f172a' }}>
                    Archive this case
                  </div>
                  <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                    Hide from main views. Legal audit record — never permanently deleted.
                  </div>
                </div>
                {caseData.archived ? (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <ArchivedBadge />
                    <RestoreButton kind="case" id={case_id}
                                    onRestored={() => window.location.reload()} />
                  </div>
                ) : (
                  <ArchiveButton kind="case" id={case_id}
                                  legalRetention
                                  testid="btn-archive-case"
                                  label="Archive case"
                                  onArchived={() => navigate('/')} />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* DOCUMENTS LIST */}
      {caseData.documents && caseData.documents.length > 0 && (
        <div className="sc-documents" data-testid="sc-documents">
          <h3>Case Documents</h3>
          <div className="sc-doc-list">
            {caseData.documents.map((doc, idx) => {
              const fname = ((doc.doc_label || doc.doc_type || 'document').toString().replace(/\s+/g, '_') + '.pdf').toLowerCase();
              return (
                <div key={idx} className="sc-doc-item">
                  <span className="sc-doc-type">{doc.doc_label || doc.doc_type}</span>
                  <span className="sc-doc-date">{formatDate(doc.created_at, '')}</span>
                  <button
                    type="button"
                    className="sc-btn sc-btn-sm"
                    style={{ background: '#0073EA', color: '#fff', border: 'none', cursor: 'pointer' }}
                    onClick={() => downloadCaseDocument(doc.file_url, fname)}
                    data-testid={`sc-doc-dl-${idx}`}
                  >
                    Download
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* STAGE TRANSITIONS */}
      {caseData.stage_transitions && caseData.stage_transitions.length > 0 && (
        <div className="sc-transitions" data-testid="sc-transitions">
          <h3>Stage History</h3>
          <div className="sc-trans-list">
            {caseData.stage_transitions.map((t, idx) => (
              <div key={idx} className="sc-trans-item">
                <span className="sc-trans-stages">Stage {t.from_stage} &rarr; {t.to_stage}</span>
                <span className="sc-trans-actor">{t.actor}</span>
                <span className="sc-trans-date">{formatDateTime(t.transitioned_at, '')}</span>
                {t.notes && <span className="sc-trans-notes">{t.notes}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FIX 5 — Inline Tenancy Agreement upload modal at Stage 6.
          Kept inside the case page so the agent never navigates away. */}
      {showTenancyUploadModal && (
        <div data-testid="tenancy-upload-modal-overlay"
             style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 200, padding: '1rem' }}
             onClick={(e) => { if (e.target === e.currentTarget) setShowTenancyUploadModal(false); }}>
          <div data-testid="tenancy-upload-modal"
               style={{ background: '#fff', borderRadius: 10,
                        padding: '1.4rem 1.6rem', width: '100%', maxWidth: 520,
                        boxShadow: '0 12px 32px rgba(15,23,42,0.25)' }}>
            <h2 style={{ margin: '0 0 0.4rem', fontSize: '1.15rem' }}>
              Upload Tenancy Agreement
            </h2>
            <p style={{ margin: '0 0 1rem', color: '#64748b', fontSize: '0.88rem' }}>
              The agreement is stored against this property and will be
              included automatically in the court bundle. PDF only.
            </p>
            <input type="file" accept="application/pdf"
                   onChange={e => setInlineTenancyFile(e.target.files?.[0] || null)}
                   data-testid="tenancy-upload-file"
                   style={{ display: 'block', marginBottom: '0.85rem' }} />
            {inlineTenancyError && (
              <div data-testid="tenancy-upload-error"
                   style={{ color: '#dc2626', fontSize: '0.85rem',
                            marginBottom: '0.65rem' }}>
                {inlineTenancyError}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button type="button"
                      onClick={() => setShowTenancyUploadModal(false)}
                      data-testid="tenancy-upload-cancel"
                      style={{ background: '#fff', color: '#475569',
                               border: '1px solid #cbd5e1',
                               padding: '0.55rem 1.1rem', borderRadius: 6,
                               cursor: 'pointer' }}>
                Cancel
              </button>
              <button type="button"
                      onClick={handleInlineTenancyUpload}
                      disabled={inlineTenancyUploading || !inlineTenancyFile}
                      data-testid="tenancy-upload-submit"
                      style={{ background: (inlineTenancyUploading || !inlineTenancyFile) ? '#94a3b8' : '#0073EA',
                               color: '#fff', border: 'none',
                               padding: '0.55rem 1.1rem', borderRadius: 6,
                               cursor: (inlineTenancyUploading || !inlineTenancyFile) ? 'not-allowed' : 'pointer',
                               fontWeight: 500 }}>
                {inlineTenancyUploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Section8Case;

/* ==========================================================================
 * Stage 6 — Route Picker (2026-05-06)
 *
 * Pre-selects the recommended court-filing route from `case.case_flow`:
 *   • case_flow === 'arrears'        → recommend PCOL
 *   • case_flow === 'other_grounds'  → recommend Paper
 * Shows a green "Recommended" badge + spec-mandated note next to the
 * recommended option. Pre-selection is a default only — the agent can
 * pick either route. Confirmation dialog (handled by parent via the
 * onConfirm callback) fires when the agent switches away from a
 * previously-saved court_route.
 * ========================================================================== */
function Stage6RoutePicker({ caseData, processing, onConfirm }) {
  const flow = caseData.case_flow;            // 'arrears' | 'other_grounds' | null
  const previously = caseData.court_route;    // 'paper' | 'pcol' | null
  // Default selection: previously-confirmed > funnel-based recommendation
  // > PCOL fallback (current legacy default).
  const defaultRoute = previously
    || (flow === 'arrears' ? 'pcol'
        : flow === 'other_grounds' ? 'paper'
        : 'pcol');
  const [selected, setSelected] = useState(defaultRoute);

  const recommended = flow === 'arrears' ? 'pcol'
                     : flow === 'other_grounds' ? 'paper'
                     : null;

  const Badge = () => (
    <span
      data-testid="sc-route-recommended-badge"
      style={{
        display: 'inline-block', marginLeft: '0.5rem',
        background: '#dcfce7', color: '#166534',
        border: '1px solid #86efac', borderRadius: 999,
        padding: '0.1rem 0.6rem', fontSize: '0.72rem',
        fontWeight: 600, letterSpacing: '0.02em',
      }}>
      Recommended
    </span>
  );

  const recNote = recommended === 'pcol'
    ? 'PCOL is the recommended filing route for rent arrears claims — faster processing and no postal delays.'
    : recommended === 'paper'
    ? 'Paper route recommended for mixed or non-rent grounds cases.'
    : null;

  const card = (route, title, body) => {
    const isSelected = selected === route;
    const isRec = recommended === route;
    return (
      <button
        type="button"
        onClick={() => setSelected(route)}
        disabled={processing}
        data-testid={`sc-route-card-${route}`}
        style={{
          textAlign: 'left',
          background: isSelected ? '#eff6ff' : '#fff',
          color: '#0f172a',
          border: `2px solid ${isSelected ? '#0073EA' : '#e2e8f0'}`,
          borderRadius: 10,
          padding: '1rem 1.15rem',
          cursor: processing ? 'wait' : 'pointer',
          width: '100%',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <strong style={{ fontSize: '1rem' }}>
            {title}
            {isRec && <Badge />}
          </strong>
          {isSelected && (
            <span style={{ fontSize: '0.78rem', color: '#1d4ed8', fontWeight: 600 }}>
              Selected
            </span>
          )}
        </div>
        <p style={{ margin: '0.45rem 0 0', fontSize: '0.86rem', color: '#475569', lineHeight: 1.5 }}>
          {body}
        </p>
        {isRec && recNote && (
          <p
            data-testid={`sc-route-recommended-note-${route}`}
            style={{ margin: '0.45rem 0 0', fontSize: '0.82rem',
                     color: '#166534', lineHeight: 1.5 }}>
            {recNote}
          </p>
        )}
      </button>
    );
  };

  return (
    <div className="sc-section" data-testid="sc-stage-6-route-picker">
      <h2>Stage 6 — Choose your court-filing route</h2>
      <p style={{ color: '#475569', fontSize: '0.92rem', lineHeight: 1.55, margin: '0 0 1rem' }}>
        Pick the route you want to use to file possession proceedings.
        You can switch later if you need to.
      </p>

      <div style={{ display: 'grid', gap: '0.75rem' }}>
        {card(
          'pcol',
          'PCOL — Possession Claim Online',
          'Online filing through the Civil Procedure Rules portal. Best for rent-arrears-only claims (Grounds 8, 10, 11).'
        )}
        {card(
          'paper',
          'Paper — Court bundle by post',
          'Print-and-post N5 + N119 + N215 + supporting documents. Required for non-rent grounds and recommended for mixed cases.'
        )}
      </div>

      <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
        <button
          type="button"
          onClick={() => onConfirm(selected)}
          disabled={processing}
          data-testid="sc-btn-confirm-route"
          style={{
            background: '#0073EA', color: '#fff', border: 'none',
            padding: '0.75rem 1.4rem', borderRadius: 6,
            fontSize: '0.95rem', fontWeight: 600,
            cursor: processing ? 'wait' : 'pointer',
          }}>
          {processing ? 'Confirming…' : 'Confirm route and continue'}
        </button>
        {previously && previously !== selected && (
          <span style={{ fontSize: '0.82rem', color: '#92400e' }}
                data-testid="sc-route-switch-warning">
            Switching from {previously.toUpperCase()} to {selected.toUpperCase()} — confirmation will be required.
          </span>
        )}
      </div>
    </div>
  );
}
